// @ts-nocheck
// Wizard + UI wiring (vanilla JS, no dependencies).
(() => {
  const STEP_IDS = [
    "est_projet",
    "est_info",
    "est_result",
    "est_soumission",
    "cfg_base",
    "cfg_shape",
    "cfg_dimensions",
    "cfg_openings",
    "cfg_stairs",
    "cfg_summary",
  ];

  const TOTAL_EST_STEPS = 3;
  const TOTAL_CFG_STEPS = 6;

  // Map product IDs (from URL hash params) to estimate presets.
  const ESTIMATE_PRODUCT_PRESETS = {
    // Ornamental aluminum railing
    "10274028028193": {
      railingType: "aluminum",
      aluminumType: "ornementale",
    },
    // Aluminum railing with balusters
    "10274039103777": {
      railingType: "aluminum",
      aluminumType: "aluminum_baluster",
    },
    // 6mm tempered glass aluminum railing
    "10274036547873": {
      railingType: "aluminum",
      aluminumType: "aluminum_glass_6mm",
    },
  };

  const ESTIMATION_SERVICE_VARIANT_ID = "52412825534753";
  const PROJECT_DETAILS_VARIANT_ID = "52412839166241";
  const INCH_STEP = 0.125;
  const CM_PER_INCH = 2.54;

  const Engine = window.Cfg && window.Cfg.Engine;
  const Preview = window.Cfg && window.Cfg.Preview;
  
  // Translation helper
  function t(key, ...args) {
    const translations = window.CfgTranslations && window.CfgTranslations.strings;
    if (!translations || !translations[key]) return key;
    let str = translations[key];
    // Replace {0}, {1}, etc. with arguments
    args.forEach((arg, i) => {
      str = str.replace(new RegExp(`\\{${i}\\}`, 'g'), String(arg));
    });
    return str;
  }

  function clampNumber(n, { min = -Infinity, max = Infinity } = {}) {
    if (Number.isNaN(n)) return n;
    return Math.min(max, Math.max(min, n));
  }

  function parseNumber(value) {
    // IMPORTANT: treat empty string as "no value" (NaN), not 0.
    // This prevents missing inputs from silently becoming 0 (e.g. square grouped side inputs).
    if (value == null) return NaN;
    if (typeof value === "number") return Number.isFinite(value) ? value : NaN;
    let s = String(value).trim();
    if (!s) return NaN;
    // Accept both "13.5" and "13,5", and ignore accidental trailing chars.
    s = s.replace(/,/g, ".");
    s = s.replace(/[^0-9eE+.\-]/g, "");
    if (!s) return NaN;
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  }

  function formatJson(obj) {
    return JSON.stringify(obj, null, 2);
  }

  function roundToEighth(inches) {
    if (!Number.isFinite(inches)) return NaN;
    return Math.round(inches / INCH_STEP) * INCH_STEP;
  }

  function inchesToCm(inches) {
    if (!Number.isFinite(inches)) return NaN;
    return inches * CM_PER_INCH;
  }

  function cmToInches(cm) {
    if (!Number.isFinite(cm)) return NaN;
    return roundToEighth(cm / CM_PER_INCH);
  }

  function roundForInput(value, unit) {
    if (!Number.isFinite(value)) return NaN;
    const precision = unit === "cm" ? 4 : 3;
    const factor = 10 ** precision;
    const rounded = Math.round(value * factor) / factor;
    return Object.is(rounded, -0) ? 0 : rounded;
  }

  function isEmptyString(s) {
    return typeof s !== "string" || s.trim().length === 0;
  }

  function getVisibleForm(root) {
    const stepEl = root.querySelector('section[data-rb-step]:not([hidden])');
    if (!stepEl) return null;
    return stepEl.querySelector("form[data-rb-form]") || null;
  }

  // -----------------------------------
  // Shopify integration layer — stubbed
  // -----------------------------------
  const ShopifyCart = {
    async syncConfigurationToCart(_configSnapshot) {
      throw new Error("Cart sync not implemented yet.");
    },
  };

  // -------------------
  // UI layer (wizard)
  // -------------------
  class RbConfigurator extends HTMLElement {
    constructor() {
      super();
      this._initialized = false;
      this._sideLengthsIn = {};
      this._edgeDistanceIn = NaN;
      this._currentUnits = "in";
      this._lastEstInfoFormspreeSignature = "";
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      if (!Engine || !Preview) {
        // Hard fail quietly in UI; the console will show the cause.
        console.error("Missing cfg-engine.js or cfg-preview.js. Ensure they are loaded before cfg-wizard.js.");
        return;
      }

      this._catalog = window.CfgCatalog || {};
      this._els = {
        titleNavEstimation: this.querySelector("[data-rb-title-nav-estimation]"),
        titleNavConfigurator: this.querySelector("[data-rb-title-nav-configurator]"),
        unitsStep: this.querySelector('[data-rb-cfg-units]'),
        currentStepEst: this.querySelector("[data-rb-step-current-est]"),
        totalStepsEst: this.querySelector("[data-rb-step-total-est]"),
        currentStepCfg: this.querySelector("[data-rb-step-current-cfg]"),
        totalStepsCfg: this.querySelector("[data-rb-step-total-cfg]"),
        divider: this.querySelector("[data-rb-divider]"),
        cfgNav: this.querySelector("[data-rb-cfg-nav]"),
        preview: this.querySelector("[data-rb-preview]"),
        shapeOtherNote: this.querySelector("[data-rb-shape-other-note]"),
        sidesContainer: this.querySelector("[data-rb-sides]"),
        openingsContainer: this.querySelector("[data-rb-openings]"),
        openingsNote: this.querySelector("[data-rb-openings-note]"),
        stairsContainer: this.querySelector("[data-rb-stairs]"),
        estDetails: this.querySelector("[data-rb-est-details]"),
        estPrice: this.querySelector("[data-rb-est-price]"),
        soumissionDetails: this.querySelector("[data-rb-soumission-details]"),
        soumissionPrice: this.querySelector("[data-rb-soumission-price]"),
        summaryDetailsPanel: this.querySelector("[data-rb-summary-details-panel]"),
        summaryDetails: this.querySelector("[data-rb-summary-details]"),
        summaryTotal: this.querySelector("[data-rb-summary-total]"),
        summaryBomTotal: this.querySelector("[data-rb-summary-bom-total]"),
        summaryStairsRow: this.querySelector("[data-rb-summary-stairs-row]"),
        summaryStairsTotal: this.querySelector("[data-rb-summary-stairs-total]"),
        summaryFinishNote: this.querySelector("[data-rb-summary-finish-note]"),
        planValidationMsg: this.querySelector("[data-rb-plan-validation-msg]"),
        dynamicEstimate: this.querySelector("[data-rb-dynamic-estimate]"),
        dynamicEstimatePrice: this.querySelector("[data-rb-dynamic-estimate-price]"),
      };

      this.addEventListener("click", (e) => this._onClick(e));
      this.addEventListener("change", (e) => this._onChange(e));
      this.addEventListener("input", (e) => this._onInput(e));
      this.addEventListener("submit", (e) => this._onSubmit(e));

      const initialStep = this.getAttribute("data-initial-step") || "est_projet";
      this._goToStep(initialStep, { replace: true, scroll: false });
      this._syncEstimateConditionalFields();
      this._syncInstallationDependentUi();
      this._syncShapeDependentUi();
      this._renderSidesInputs();
      this._renderOpenings();
      this._renderStairs();
      this._syncSummaryFilesVisibility();

      // Seed internal canonical lengths from any existing values (in inches by default).
      this._seedSideLengthsFromInputs();
      this._syncEffectiveLengths();
      this._syncUnitLabels();

      // React to unit toggle (in / cm) used for sides and edge distance.
      document.addEventListener("unit-toggle:change", (event) => {
        const detail = event.detail || {};
        if (detail.name !== "cfg_units") return;
        const newUnit = detail.unit === "cm" ? "cm" : "in";
        const oldUnit = this._currentUnits || "in";
        if (newUnit === oldUnit) return;

        this._convertDimensionInputsUnits({ from: oldUnit, to: newUnit });
        this._convertOpeningsAndStairsDisplayedUnits({ from: oldUnit, to: newUnit });
        this._currentUnits = newUnit;

        this._syncUnitLabels();
        this._syncEffectiveLengths();
        this._renderOpenings();
        this._renderStairs();
        this._renderPreview();
      });
    }

    _onClick(e) {
      const target = e.target;
      if (!(target instanceof Element)) return;

      // Close dialog when clicking the backdrop
      if (target instanceof HTMLDialogElement && target.classList.contains("q-pop-dialog") && target.open) {
        target.close();
        return;
      }

      const navBtn = target.closest("[data-rb-nav-to]");
      if (navBtn) {
        e.preventDefault();
        const to = navBtn.getAttribute("data-rb-nav-to") || "";
        this._handleCfgNav(to);
        return;
      }

      const infoBtn = target.closest("[data-rb-info]");
      if (infoBtn) {
        e.preventDefault();
        const key = infoBtn.getAttribute("data-rb-info") || "";
        this._openInfo(key);
        return;
      }

      const infoClose = target.closest("[data-rb-info-close]");
      if (infoClose) {
        e.preventDefault();
        const dlg = infoClose.closest("dialog");
        if (dlg && typeof dlg.close === "function") dlg.close();
        return;
      }

      const stairsOnlyLink = target.closest("[data-rb-link-stairs-only]");
      if (stairsOnlyLink instanceof HTMLAnchorElement) {
        e.preventDefault();
        const railingType = this._getValue("est_railing_type");
        const path = railingType === "aluminum" ? "/products/escaliers-aluminium" : "/products/escaliers-verre";
        window.location.href = path;
        return;
      }

      const nextBtn = target.closest("[data-rb-next]");
      if (nextBtn) {
        e.preventDefault();
        this._handleNext(nextBtn);
        return;
      }

      const backBtn = target.closest("[data-rb-back]");
      if (backBtn) {
        e.preventDefault();
        this._handleBack();
        return;
      }

      const downloadPdfBtn = target.closest("[data-rb-download-pdf]");
      if (downloadPdfBtn) {
        e.preventDefault();
        this._handleDownloadPdf(downloadPdfBtn);
        return;
      }

      const saveConfigBtn = target.closest("[data-rb-save-config]");
      if (saveConfigBtn) {
        e.preventDefault();
        const url = this._getConfigSaveUrl();
        if (typeof navigator.clipboard !== "undefined" && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).then(() => {
            const label = saveConfigBtn.getAttribute("data-rb-save-config-label") || t("copyLinkPrompt") || "Link copied";
            const orig = saveConfigBtn.textContent;
            saveConfigBtn.textContent = label;
            setTimeout(() => { saveConfigBtn.textContent = orig; }, 2000);
          }).catch(() => { window.prompt(t("copyLinkPrompt") || "Copy this link:", url); });
        } else {
          window.prompt(t("copyLinkPrompt") || "Copy this link:", url);
        }
        return;
      }

      const actionBtn = target.closest("[data-rb-action]");
      if (actionBtn) {
        e.preventDefault();
        const action = actionBtn.getAttribute("data-rb-action");
        this._handleAction(action, actionBtn);
      }
    }

    _onChange(e) {
      const target = e.target;
      if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement))
        return;

      if (target.name === "est_has_doors" || target.name === "est_has_stairs") {
        this._syncEstimateConditionalFields();
      }
      if (target.name === "est_height") {
        const heightError = this.querySelector("[data-rb-error=\"est_height\"]");
        if (heightError) heightError.hidden = true;
      }
      if (target.name === "est_railing_type" || target.name === "est_aluminum_type") {
        this._syncEstimateConditionalFields();
        this._syncEstimateResultActions();
      }
      if (target.name === "cfg_height" || (target.name && target.name.startsWith("stairs_"))) {
        this._renderStairs();
      }

      if (target.name === "est_installation" || target.name === "cfg_installation") {
        this._syncInstallationDependentUi(target.closest('section[data-rb-step]') || this);
      }

      if (target.name === "cfg_plan_validation") {
        this._syncSummaryFilesVisibility();
      }

      if (target.name === "cfg_shape") {
        const nextShape = this._getValue("cfg_shape");
        const prevShape = this._lastShape;
        const shapeChanged = !isEmptyString(nextShape) && nextShape !== prevShape;
        this._lastShape = nextShape;
        this._syncShapeDependentUi();
        this._renderSidesInputsWithOptions({ reset: shapeChanged });
        this._syncSideInputMinimums();
        this._renderOpenings();
        this._syncEffectiveLengths();
        this._renderPreview();
      }

      if (target.closest("[data-rb-openings]")) {
        if (target.name && target.name.startsWith("opening_side_")) {
          const row = target.closest("[data-rb-opening-row]");
          if (row instanceof HTMLElement) {
            this._syncOpeningRowDefaults(row);
            this._syncOpeningRowComputed(row);
          }
        }
        this._renderOpenings();
        this._renderPreview();
      }

      if (target.name === "cfg_edge_distance_in") {
        this._syncSideInputMinimums();
        this._syncEffectiveLengths();
        this._renderPreview();
      }

      // Recompute dynamic estimate when configurator fields change
      if (this._isConfiguratorStep(this._getCurrentStepId() || "")) {
        this._updateDynamicEstimate();
      }
    }

    _onInput(e) {
      const target = e.target;
      if (!(target instanceof HTMLInputElement)) return;

      this._sanitizeNonNegativeNumberInput(target);

      if (target.name && target.name.startsWith("cfg_side_")) {
        this._updateSideLengthFromInput(target);
        this._syncEffectiveLengths();
        this._renderOpenings();
        this._renderPreview();
      }
      if (target.name === "cfg_edge_distance_in") {
        this._updateEdgeDistanceFromInput(target);
        this._syncEffectiveLengths();
        this._renderOpenings();
        this._renderPreview();
      }
      if (target.name && target.name.startsWith("stairs_")) {
        this._renderStairs();
      }

      if (target.closest("[data-rb-openings]")) {
        if (target.name && (target.name.startsWith("opening_x_") || target.name.startsWith("opening_a1_"))) {
          const row = target.closest("[data-rb-opening-row]");
          if (row instanceof HTMLElement) {
            this._syncOpeningRowComputed(row, { isLiveInput: true, sourceInput: target });
          }
          this._renderPreview();
        }
      }

      if (this._isConfiguratorStep(this._getCurrentStepId() || "")) {
        this._updateDynamicEstimate();
      }
    }

    _onSubmit(e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches("form[data-rb-form]")) return;

      // Prevent browser form submit/reload; wizard navigation is JS-driven.
      e.preventDefault();

      // Keep Enter key useful: trigger step progression when a Next button exists.
      const nextBtn = form.querySelector("[data-rb-next]");
      if (nextBtn instanceof HTMLElement) {
        this._handleNext(nextBtn);
      }
    }

    _sanitizeNonNegativeNumberInput(input) {
      // Prevent negative numbers even when user types '-' manually.
      if (!(input instanceof HTMLInputElement)) return;
      if (input.type !== "number") return;

      const raw = input.value;
      if (raw === "" || raw == null) return;

      // If the user is mid-typing a negative sign, clear it.
      if (raw.trim() === "-") {
        input.value = "";
        return;
      }

      const n = Number(raw);
      if (!Number.isFinite(n)) return;
      if (n < 0) input.value = "0";
    }

    _seedSideLengthsFromInputs() {
      const shape = this._getValue("cfg_shape");
      const groups = this._getSideInputGroupsForShape(shape);
      for (const group of groups) {
        const input = this.querySelector(`input[name="${CSS.escape(group.name)}"]`);
        if (!(input instanceof HTMLInputElement)) continue;
        this._updateSideLengthFromInput(input);
      }
      const edgeInput = this.querySelector('input[name="cfg_edge_distance_in"]');
      if (edgeInput instanceof HTMLInputElement) {
        this._updateEdgeDistanceFromInput(edgeInput);
      }
    }

    _updateSideLengthFromInput(input) {
      if (!(input instanceof HTMLInputElement) || !input.name.startsWith("cfg_side_")) return;
      const n = parseNumber(input.value);
      if (!Number.isFinite(n)) return;
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
      const inches = units === "cm" ? cmToInches(n) : n;
      this._sideLengthsIn[input.name] = inches;
    }

    _updateEdgeDistanceFromInput(input) {
      if (!(input instanceof HTMLInputElement) || input.name !== "cfg_edge_distance_in") return;
      const n = parseNumber(input.value);
      if (!Number.isFinite(n)) return;
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
      const inches = units === "cm" ? cmToInches(n) : n;
      this._edgeDistanceIn = inches;
    }

    _currentUnitsValue() {
      return this._getValue("cfg_units") === "cm" ? "cm" : "in";
    }

    _displayToInches(value) {
      const n = parseNumber(value);
      if (!Number.isFinite(n)) return NaN;
      return this._currentUnitsValue() === "cm" ? cmToInches(n) : n;
    }

    _inchesToDisplay(inches) {
      if (!Number.isFinite(inches)) return NaN;
      const units = this._currentUnitsValue();
      const display = units === "cm" ? inchesToCm(inches) : inches;
      return roundForInput(display, units);
    }

    _unitizeLabel(text) {
      const raw = String(text || "");
      const units = this._currentUnitsValue();
      const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);
      if (units === "cm") {
        return raw
          .replace(/\((po|in)\)/gi, "(cm)")
          .replace(/\b(po|in)\b/gi, "cm");
      }
      return raw
        .replace(/\(cm\)/gi, isFr ? "(po)" : "(in)")
        .replace(/\bcm\b/gi, isFr ? "po" : "in");
    }

    _convertOpeningsAndStairsDisplayedUnits({ from, to }) {
      if (from === to) return;
      const toCm = to === "cm";
      const convert = (n) => (toCm ? n * CM_PER_INCH : n / CM_PER_INCH);

      const inputs = this.querySelectorAll(
        'input[type="number"][name^="opening_x_"], input[type="number"][name^="opening_a1_"], input[type="number"][name^="stairs_length_in_"], input[type="number"][name^="stairs_height_in_"], input[type="number"][name^="stairs_diagonal_in_"]'
      );
      for (const input of inputs) {
        if (!(input instanceof HTMLInputElement)) continue;
        const n = parseNumber(input.value);
        if (Number.isFinite(n)) input.value = String(roundForInput(convert(n), toCm ? "cm" : "in"));
        const min = parseNumber(input.min);
        const max = parseNumber(input.max);
        if (Number.isFinite(min)) input.min = String(roundForInput(convert(min), toCm ? "cm" : "in"));
        if (Number.isFinite(max)) input.max = String(roundForInput(convert(max), toCm ? "cm" : "in"));
        input.step = toCm ? String(INCH_STEP * CM_PER_INCH) : "0.125";
      }
    }

    _normalizeNumberInputValue(input) {
      if (!(input instanceof HTMLInputElement) || input.type !== "number") return;
      let n = parseNumber(input.value);
      if (!Number.isFinite(n)) return;

      const min = parseNumber(input.min);
      const max = parseNumber(input.max);
      const step = parseNumber(input.step);

      if (Number.isFinite(min)) n = Math.max(min, n);
      if (Number.isFinite(max)) n = Math.min(max, n);
      if (Number.isFinite(step) && step > 0) {
        const base = Number.isFinite(min) ? min : 0;
        n = base + Math.round((n - base) / step) * step;
      }

      // Keep a stable number of decimals based on step precision.
      const stepText = (input.step || "").trim();
      const stepDecimals = stepText.includes(".") ? stepText.split(".")[1].length : 0;
      const precision = Math.min(Math.max(stepDecimals, 0), 6);
      const rounded = Number.isFinite(n) ? Number(n.toFixed(precision)) : n;
      input.value = String(rounded);
    }

    _normalizeAllNumberInputs() {
      const inputs = this.querySelectorAll('input[type="number"]');
      for (const input of inputs) {
        this._normalizeNumberInputValue(input);
      }
    }

    _handleNext(nextBtn) {
      const currentStepId = this._getCurrentStepId();

      if (currentStepId === "est_projet") {
        const heightError = this.querySelector("[data-rb-error=\"est_height\"]");
        if (!this._getValue("est_height")) {
          if (heightError) heightError.hidden = false;
          const firstHeightRadio = this.querySelector('input[name="est_height"]');
          if (firstHeightRadio instanceof HTMLInputElement) firstHeightRadio.focus();
          return;
        }
        if (heightError) heightError.hidden = true;
      }

      const form = getVisibleForm(this);
      if (form && !form.reportValidity()) return;

      if (currentStepId === "cfg_openings") {
        if (!this._validateOpenings()) return;
      }

      // Custom CTA for "Other" shape
      if (currentStepId === "cfg_shape") {
        const shape = this._getValue("cfg_shape");
        if (shape === "other") {
          window.location.href = nextBtn.getAttribute("data-rb-shape-other-url") || "/pages/contact/";
          return;
        }
      }

      // Custom label support (used for "Obtenir mon estimation")
      const nextLabel = nextBtn.getAttribute("data-rb-next-label");
      if (nextLabel) nextBtn.textContent = nextLabel;

      const nextStepId = this._computeNextStepId(currentStepId);
      if (!nextStepId) return;

      if (nextStepId === "est_soumission") {
        this._populateSoumissionPanel();
      }

      if (nextStepId === "cfg_dimensions") {
        this._renderSidesInputs(); // ensure side inputs are rendered
        this._syncShapeDependentUi(); // sync edge distance visibility for straight shapes
      }
      if (currentStepId === "cfg_dimensions") {
        this._renderOpenings(); // ensure side options are up-to-date
      }

      if (nextStepId === "cfg_summary") {
        this._populateConfiguratorSummary();
      }

      this._goToStep(nextStepId);
    }

    _handleBack() {
      const currentStepId = this._getCurrentStepId();
      const prev = this._computePrevStepId(currentStepId);
      if (prev) this._goToStep(prev);
    }

    _handleAction(action, actionButton) {
      switch (action) {
        case "use_configurator": {
          const qe = this._collectEstimateInput();
          const glassUrl = actionButton?.getAttribute("data-rb-cfg-url");
          // Fence routing — uncomment when launching:
          // const fenceUrl = actionButton?.getAttribute("data-rb-cfg-url-fence");
          // const cfgUrl = qe.aluminumType === "ornementale" && fenceUrl ? fenceUrl : glassUrl;
          const cfgUrl = glassUrl;
          if (cfgUrl) {
            const params = new URLSearchParams();
            params.set("step", "cfg_base");
            if (Number.isFinite(qe.heightIn)) params.set("cfg_height", String(qe.heightIn));
            const finish = qe.railingType === "aluminum"
              ? "stainless"
              : (qe.finish === "matte_black" ? "matte_black" : "stainless");
            params.set("cfg_finish", finish);
            if (qe.aluminumType) params.set("est_aluminum_type", qe.aluminumType);
            window.location.href = `${window.location.origin}${cfgUrl}?${params.toString()}`;
          } else {
            this._prefillConfiguratorFromEstimate();
            this._goToStep("cfg_base");
          }
          break;
        }
        case "design_service": {
          this._goToStep("est_soumission");
          break;
        }
        case "add_opening": {
          this._addOpeningRow();
          break;
        }
        case "add_stairs": {
          this._addStairsRow();
          break;
        }
        case "sync_cart": {
          window.alert(t('syncCart'));
          break;
        }
        case "add_to_cart": {
          this._handleAddToCart(actionButton);
          break;
        }
        case "submit_project": {
          const soumissionStep = this.querySelector('section[data-rb-step="est_soumission"]');
          const soumissionForm = soumissionStep ? soumissionStep.querySelector('form[data-rb-form]') : null;
          const installationInput = soumissionForm
            ? soumissionForm.querySelector('input[name="est_installation"]:checked')
            : null;
          const installationError = soumissionForm
            ? soumissionForm.querySelector("#est-installation-error")
            : null;

          if (!installationInput) {
            if (installationError) {
              installationError.hidden = false;
              installationError.scrollIntoView({ behavior: "smooth", block: "nearest" });
            }
            break;
          } else if (installationError) {
            installationError.hidden = true;
          }

          if (actionButton instanceof HTMLButtonElement) actionButton.disabled = true;

          this._sendEstInfoToFormspree()
            .then(() => {
              const formPanel = soumissionStep ? soumissionStep.querySelector(".est_soumission-panel--form") : null;
              if (formPanel) {
                const isFr = window.CfgTranslations && window.CfgTranslations.locale === "fr";
                const ctaTemplate = this.querySelector("[data-rb-configurator-cta-template]");
                const ctaMarkup = ctaTemplate instanceof HTMLTemplateElement ? ctaTemplate.innerHTML : "";
                formPanel.innerHTML = `<div class="est_soumission-confirmation">
                  <p>${isFr
                    ? "Merci ! Votre projet a été soumis avec succès. Nous vous contacterons sous peu avec votre estimation."
                    : "Thank you! Your project has been submitted successfully. We will contact you shortly with your estimate."
                  }</p>
                  ${ctaMarkup}
                </div>`;
              }
            })
            .catch((err) => {
              if (actionButton instanceof HTMLButtonElement) actionButton.disabled = false;
              console.warn("Formspree submit failed:", err);
              const isFr = window.CfgTranslations && window.CfgTranslations.locale === "fr";
              window.alert(isFr ? "Une erreur s'est produite. Veuillez réessayer." : "An error occurred. Please try again.");
            });
          break;
        }
        default:
          break;
      }
    }

    _computeNextStepId(currentStepId) {
      switch (currentStepId) {
        case "est_projet":
          return "est_info";
        case "est_info":
          return "est_soumission";
        case "est_result":
          // user decides via buttons
          return null;
        case "cfg_base":
          return "cfg_shape";
        case "cfg_shape":
          return "cfg_dimensions";
        case "cfg_dimensions":
          return "cfg_openings";
        case "cfg_openings":
          return "cfg_stairs";
        case "cfg_stairs":
          return "cfg_summary";
        case "cfg_summary":
          return null;
        default:
          return null;
      }
    }

    _computePrevStepId(currentStepId) {
      switch (currentStepId) {
        case "cfg_base":
          return "est_result";
        case "est_info":
          return "est_projet";
        case "est_result":
          return "est_info";
        case "est_soumission":
          return "est_info";
        case "cfg_shape":
          return "cfg_base";
        case "cfg_dimensions":
          return "cfg_shape";
        case "cfg_openings":
          return "cfg_dimensions";
        case "cfg_stairs":
          return "cfg_openings";
        case "cfg_summary":
          return "cfg_stairs";
        default:
          return null;
      }
    }

    _getCurrentStepId() {
      const current = this.querySelector('section[data-rb-step]:not([hidden])');
      return current ? current.getAttribute("data-rb-step") : null;
    }

    _goToStep(stepId, { replace = false, scroll = true } = {}) {
      const previousStepId = this._currentStepId || null;
      const sections = this.querySelectorAll("section[data-rb-step]");
      for (const s of sections) s.hidden = s.getAttribute("data-rb-step") !== stepId;
      this._currentStepId = stepId;

      // Show the standalone units toggle only on specific configurator steps.
      if (this._els.unitsStep) {
        const showUnits =
          stepId === "cfg_dimensions" ||
          stepId === "cfg_openings" ||
          stepId === "cfg_stairs";
        this._els.unitsStep.hidden = !showUnits;
      }

      if (stepId === "est_soumission") {
        this._populateSoumissionPanel();
      }
      if (stepId === "cfg_dimensions") {
        this._renderSidesInputs(); // ensure side inputs are rendered
        this._syncShapeDependentUi(); // sync edge distance visibility for straight shapes
        // Seed canonical inch values from the freshly rendered defaults
        this._seedSideLengthsFromInputs();
        this._syncEffectiveLengths();
      }
      if (stepId === "cfg_summary") {
        this._populateConfiguratorSummary();
        this._syncSummaryFilesVisibility();
      }

      this._updateStepIndicator(stepId);
      this._syncCfgNav(stepId);
      this._syncEstimateResultActions();
      this._syncPreview(stepId);

      // Keep URL hash in sync for refresh/debugging (no router)
      try {
        const hash = `#${stepId}`;
        if (replace) history.replaceState(null, "", hash);
        else history.pushState(null, "", hash);
      } catch (_) {
        // ignore
      }

      if (scroll && previousStepId !== stepId) {
        requestAnimationFrame(() => this._scrollToWizardTop());
      }
    }

    _scrollToWizardTop() {
      if (typeof window === "undefined" || !this.isConnected) return;
      const rect = this.getBoundingClientRect();
      const y = rect.top + window.scrollY - 16;
      window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
    }

    _isConfiguratorStep(stepId) {
      return typeof stepId === "string" && stepId.startsWith("cfg_");
    }

    _getCfgStepNumber(stepId) {
      if (stepId === "cfg_base") return 1;
      if (stepId === "cfg_shape") return 2;
      if (stepId === "cfg_dimensions") return 3;
      if (stepId === "cfg_openings") return 4;
      if (stepId === "cfg_stairs") return 5;
      if (stepId === "cfg_summary") return 6;
      return null;
    }

    _syncCfgNav(stepId) {
      const nav = this._els.cfgNav;
      if (!nav) return;

      const isCfg = this._isConfiguratorStep(stepId);
      nav.hidden = !isCfg;
      if (!isCfg) return;

      const currentNum = this._getCfgStepNumber(stepId) || 1;
      const buttons = nav.querySelectorAll("[data-rb-nav-to]");
      const listItems = nav.querySelectorAll("li");
      
      for (let i = 0; i < buttons.length; i++) {
        const btn = buttons[i];
        const li = listItems[i];
        if (!(btn instanceof HTMLButtonElement) || !li) continue;
        
        const to = btn.getAttribute("data-rb-nav-to") || "";
        const num = this._getCfgStepNumber(to);
        const enabled = typeof num === "number" && num <= currentNum;
        
        btn.disabled = !enabled;
        if (to === stepId) {
          btn.setAttribute("aria-current", "step");
        } else {
          btn.removeAttribute("aria-current");
        }
        
        // Set step status for border styling
        if (typeof num === "number") {
          if (num < currentNum) {
            li.setAttribute("data-cfg-step-status", "completed");
          } else if (num === currentNum) {
            li.setAttribute("data-cfg-step-status", "current");
          } else {
            li.setAttribute("data-cfg-step-status", "future");
          }
        }
      }
    }

    _handleCfgNav(targetStepId) {
      const current = this._getCurrentStepId();
      if (!this._isConfiguratorStep(current)) return;
      if (!this._isConfiguratorStep(targetStepId)) return;

      const currentNum = this._getCfgStepNumber(current) || 1;
      const targetNum = this._getCfgStepNumber(targetStepId);
      if (typeof targetNum !== "number") return;

      // Backward-only navigation: prevent skipping ahead.
      if (targetNum > currentNum) return;

      this._goToStep(targetStepId);
    }

    _syncPreview(stepId) {
      const mount = this._els.preview;
      const summaryPanel = this._els.summaryDetailsPanel;
      const dynamicEstimate = this._els.dynamicEstimate;
      const isSummary = stepId === "cfg_summary";

      if (summaryPanel) summaryPanel.hidden = !isSummary;
      const isCfg = this._isConfiguratorStep(stepId);
      const cfgStepNum = this._getCfgStepNumber(stepId);
      const showPreview = isCfg && typeof cfgStepNum === "number" && cfgStepNum >= 2 && !isSummary; // cfg_shape+

      if (mount) mount.hidden = !showPreview;
      if (dynamicEstimate) dynamicEstimate.hidden = !(isCfg && typeof cfgStepNum === "number" && cfgStepNum >= 3 && cfgStepNum <= 5);

      if (!showPreview || !mount) return;
      this._previewStepId = stepId;

      if (!this._previewSvg) {
        this._previewSvg = Preview.createSvg();
        mount.innerHTML = "";
        mount.appendChild(this._previewSvg);
      }

      this._renderPreview();
    }

    _renderPreview() {
      if (!this._previewSvg) return;
      const input = this._collectConfiguratorInput();
      const plan = Engine.normalizePlan(input);
      const mode = this._previewStepId === "cfg_shape" ? "shape-only" : "full";
      const engineOut = mode === "shape-only" ? null : Engine.computeRecommendedBom(input, this._catalog);
      Preview.render(this._previewSvg, plan, { mode, layout: engineOut?.layoutSummary, catalog: this._catalog });
    }

    async _handleDownloadPdf(buttonEl) {
      const Pdf = window.Cfg?.Pdf;
      if (!Pdf || typeof Pdf.generateConfigPdf !== "function") {
        window.alert(t("downloadPdfError"));
        return;
      }
      const input = this._collectConfiguratorInput();
      const plan = Engine.normalizePlan(input);
      const engineOut = Engine.computeRecommendedBom(input, this._catalog);
      if (!plan) {
        window.alert(t("downloadPdfError"));
        return;
      }
      this._previewStepId = "cfg_summary";
      if (!this._previewSvg && this._els.preview) {
        this._previewSvg = Preview.createSvg();
        this._els.preview.innerHTML = "";
        this._els.preview.appendChild(this._previewSvg);
      }
      this._renderPreview();
      const configUrl = this._getConfigSaveUrl();
      const isFr = !!(window.CfgTranslations?.isFr);
      if (buttonEl instanceof HTMLButtonElement) buttonEl.disabled = true;
      try {
        const blob = await Pdf.generateConfigPdf({
          svg: this._previewSvg,
          plan,
          layout: engineOut?.layoutSummary,
          configUrl,
          isFr,
        });
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = "configuration-rampe.pdf";
        a.click();
        URL.revokeObjectURL(a.href);
      } catch (err) {
        const msg = err && typeof err === "object" && "message" in err ? String(err.message) : t("downloadPdfError");
        window.alert(msg);
      } finally {
        if (buttonEl instanceof HTMLButtonElement) buttonEl.disabled = false;
      }
    }

    _updateStepIndicator(stepId) {
      const isCfg = this._isConfiguratorStep(stepId);
      const hideEstimation = isCfg || stepId === "est_soumission";

      // Show/hide appropriate title nav block
      if (this._els.titleNavEstimation) {
        this._els.titleNavEstimation.hidden = hideEstimation;
      }
      if (this._els.titleNavConfigurator) {
        this._els.titleNavConfigurator.hidden = !isCfg;
      }

      if (this._els.divider) {
        this._els.divider.hidden = !isCfg;
      }

      // Update step numbers for the visible block
      const stepNum = (() => {
        if (!isCfg) {
          if (stepId === "est_projet") return 1;
          if (stepId === "est_info") return 2;
          if (stepId === "est_result" || stepId === "est_soumission") return 3;
          return 1;
        }
        if (stepId === "cfg_base") return 1;
        if (stepId === "cfg_shape") return 2;
        if (stepId === "cfg_dimensions") return 3;
        if (stepId === "cfg_openings") return 4;
        if (stepId === "cfg_stairs") return 5;
        if (stepId === "cfg_summary") return 6;
        return 1;
      })();

      if (this._els.currentStepEst) {
        this._els.currentStepEst.textContent = String(stepNum);
      }
      if (this._els.totalStepsEst) {
        this._els.totalStepsEst.textContent = String(TOTAL_EST_STEPS);
      }
      if (this._els.currentStepCfg) {
        this._els.currentStepCfg.textContent = String(stepNum);
      }
      if (this._els.totalStepsCfg) {
        this._els.totalStepsCfg.textContent = String(TOTAL_CFG_STEPS);
      }

      // Update dynamic estimate visibility & value when entering configurator steps
      if (isCfg) {
        this._updateDynamicEstimate();
      }
    }

    _getValue(name) {
      const el = this.querySelector(`[name="${CSS.escape(name)}"]`);
      if (!el) return "";
      if (el instanceof HTMLInputElement && el.type === "radio") {
        const checked = this.querySelector(`[name="${CSS.escape(name)}"]:checked`);
        return checked ? checked.value : "";
      }
      return el.value ?? "";
    }

    _setValue(name, value) {
      const el = this.querySelector(`[name="${CSS.escape(name)}"]`);
      if (!el) return;
      if (el instanceof HTMLInputElement && el.type === "radio") {
        const target = this.querySelector(`[name="${CSS.escape(name)}"][value="${CSS.escape(String(value))}"]`);
        if (target instanceof HTMLInputElement) target.checked = true;
        return;
      }
      el.value = String(value);
    }

    _syncShapeDependentUi() {
      const shape = this._getValue("cfg_shape");
      const isOther = shape === "other";
      const isStraight = shape === "straight";
      if (this._els.shapeOtherNote) this._els.shapeOtherNote.hidden = !isOther;

      // Swap the Next button into a contact CTA when shape == other
      const shapeStep = this.querySelector('section[data-rb-step="cfg_shape"]');
      if (shapeStep) {
        const nextBtn = shapeStep.querySelector("[data-rb-next]");
        if (nextBtn instanceof HTMLButtonElement) {
          const defaultLabel = nextBtn.getAttribute("data-rb-default-label") || "Next step";
          const otherLabel = nextBtn.getAttribute("data-rb-shape-other-label") || defaultLabel;
          nextBtn.textContent = isOther ? otherLabel : defaultLabel;
          nextBtn.disabled = false;
        }
      }

      // Hide edge distance input when shape is straight
      const edgeDistanceP = this.querySelector('[data-rb-edge-distance]');
      if (edgeDistanceP) {
        edgeDistanceP.hidden = isStraight;
        const edgeInput = edgeDistanceP.querySelector('input[name="cfg_edge_distance_in"]');
        if (edgeInput instanceof HTMLInputElement) {
          edgeInput.required = !isStraight;
        }
      }

      // Hide effective length readouts when shape is straight
      const effectiveReadouts = this.querySelectorAll('[data-rb-side-effective]');
      for (const el of effectiveReadouts) {
        if (el instanceof HTMLElement) el.hidden = isStraight;
      }
    }

    _syncSummaryFilesVisibility() {
      const filesSection = this.querySelector("[data-rb-summary-files]");
      if (!(filesSection instanceof HTMLElement)) return;
      const val = this._getValue("cfg_plan_validation");
      filesSection.hidden = val !== "yes";
    }

    _getSideLabelsForShape(shape) {
      return Engine.getSideLabelsForShape(shape);
    }

    _getSideInputGroupsForShape(shape) {
      // Used for the dimensions UI only.
      if (shape === "square") {
        return [
          { key: "AC", labels: ["A", "C"], name: "cfg_side_AC_in", title: t('lengthSides', 'A', 'C') },
          { key: "BD", labels: ["B", "D"], name: "cfg_side_BD_in", title: t('lengthSides', 'B', 'D') },
        ];
      }
      return this._getSideLabelsForShape(shape).map((label) => ({
        key: label,
        labels: [label],
        name: `cfg_side_${label}_in`,
        title: t('lengthSide', label),
      }));
    }

    _renderSidesInputs() {
      return this._renderSidesInputsWithOptions({ reset: false });
    }

    _renderSidesInputsWithOptions({ reset = false } = {}) {
      const container = this._els.sidesContainer;
      if (!container) return;

      const shape = this._getValue("cfg_shape");
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
      const groups = this._getSideInputGroupsForShape(shape);
      const edgeDistanceIn = this._edgeDistanceIn;
      const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;

      const previousValues = new Map();
      if (!reset) {
        for (const input of container.querySelectorAll("input[name^='cfg_side_']")) {
          if (input instanceof HTMLInputElement && !isEmptyString(input.value)) {
            previousValues.set(input.name, input.value);
          }
        }
      }

      container.innerHTML = "";
      if (groups.length === 0) {
        container.textContent = t('selectShape');
        return;
      }

      const frag = document.createDocumentFragment();
      for (const group of groups) {
        const p = document.createElement("p");
        const name = group.name;
        const input = document.createElement("input");
        input.type = "number";
        input.name = name;
        input.required = true;
        const minRaw = Math.max(
          0,
          ...group.labels.map((label) => {
            const { cornerAtStart, cornerAtEnd } = this._cornerFlagsForSide(shape, label);
            const requiredEffective = 4 + (cornerAtStart ? 0.75 : 0) + (cornerAtEnd ? 0.75 : 0);
            const requiredRaw = requiredEffective + (cornerAtStart ? edge : 0) + (cornerAtEnd ? edge : 0);
            return requiredRaw;
          })
        );
        input.min = String(units === "cm" ? inchesToCm(minRaw) : minRaw);
        // Step is 1/8" in inches, or the exact converted value in cm.
        input.step = units === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
        input.inputMode = "decimal";
        input.placeholder = "0";

        const prev = previousValues.get(name);
        if (!isEmptyString(prev)) {
          const prevNum = parseNumber(prev);
          if (Number.isFinite(prevNum) && prevNum < minRaw) {
            input.value = String(units === "cm" ? inchesToCm(minRaw) : minRaw);
          } else {
            input.value = prev;
          }
        } else {
          // Default before user enters anything.
          const firstLabel = group.labels[0];
          const defVal = Engine.getDefaultSideLengthIn(shape, firstLabel);
          input.value = String(Math.max(defVal, minRaw));
        }

        const lab = document.createElement("label");
        const titleSpan = document.createElement("span");
        // Strip any existing unit suffix like " (po)" or " (in)" from the title,
        // since we add a dedicated unit span next.
        const baseTitle = group.title.replace(/\s*\((po|in)\)$/i, "");
        titleSpan.textContent = baseTitle + " ";
        const unitSpan = document.createElement("span");
        unitSpan.setAttribute("data-rb-unit-label", "length");
        const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);
        unitSpan.textContent =
          units === "cm" ? (isFr ? "(cm)" : "(cm)") : (isFr ? "(po)" : "(in)");
        lab.append(titleSpan, unitSpan, " ", input);
        p.appendChild(lab);

        // Effective length readout(s) - always create but hide for straight shapes
        // For square shapes with combined sides, only show one readout per group
        const br = document.createElement("br");
        p.appendChild(br);
        // Use first label for the readout (for square, A&C and B&D share the same effective length)
        const firstLabel = group.labels[0];
        const effective = document.createElement("small");
        effective.setAttribute("data-rb-side-effective", firstLabel);
        effective.textContent = "";
        p.appendChild(effective);
        p.appendChild(document.createElement("br"));

        frag.appendChild(p);
      }
      container.appendChild(frag);
    }

    _syncSideInputMinimums() {
      const container = this._els.sidesContainer;
      if (!container) return;
      const shape = this._getValue("cfg_shape");
      const groups = this._getSideInputGroupsForShape(shape);
      const edgeDistanceIn = this._edgeDistanceIn;
      const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
      for (const group of groups) {
        const input = container.querySelector(`input[name="${CSS.escape(group.name)}"]`);
        if (!(input instanceof HTMLInputElement)) continue;
        const minRaw = Math.max(
          0,
          ...group.labels.map((label) => {
            const { cornerAtStart, cornerAtEnd } = this._cornerFlagsForSide(shape, label);
            const requiredEffective = 4 + (cornerAtStart ? 0.75 : 0) + (cornerAtEnd ? 0.75 : 0);
            const requiredRaw = requiredEffective + (cornerAtStart ? edge : 0) + (cornerAtEnd ? edge : 0);
            return requiredRaw;
          })
        );
        input.min = String(units === "cm" ? inchesToCm(minRaw) : minRaw);
        // Keep step in sync here as well in case inputs already exist.
        input.step = units === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
        const current = parseNumber(input.value);
        if (Number.isFinite(current) && current < minRaw) {
          const displayMin = units === "cm" ? inchesToCm(minRaw) : minRaw;
          input.value = String(displayMin);
        }
      }
    }

    _syncUnitLabels() {
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
      const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);

      // Update side length labels (A, B, C, etc.)
      const sideUnitSpans = this.querySelectorAll('[data-rb-unit-label="length"]');
      sideUnitSpans.forEach((span) => {
        if (!(span instanceof HTMLElement)) return;
        span.textContent =
          units === "cm" ? (isFr ? "(cm)" : "(cm)") : (isFr ? "(po)" : "(in)");
      });

      // Update edge distance legend label "(po)" / "(in)" / "(cm)"
      const edgeFieldset = this.querySelector("[data-rb-edge-distance]");
      if (edgeFieldset) {
        const label = edgeFieldset.querySelector("label");
        if (label) {
          const text =
            units === "cm"
              ? (isFr ? "Distance du bord (cm)" : "Edge Distance (cm)")
              : (isFr ? "Distance du bord (po)" : "Edge Distance (in)");
          // Replace just the text content of the label's first child node.
          const firstChild = label.firstChild;
          if (firstChild && firstChild.nodeType === Node.TEXT_NODE) {
            firstChild.textContent = text + " ";
          } else {
            label.insertBefore(document.createTextNode(text + " "), label.firstChild || null);
          }
        }
      }
    }

    _convertDimensionInputsUnits({ from, to }) {
      const container = this._els.sidesContainer;
      if (!container) return;
      const shape = this._getValue("cfg_shape");
      const groups = this._getSideInputGroupsForShape(shape);
      const toUnits = to === "cm" ? "cm" : "in";

      // Convert each side input based on canonical inches stored in _sideLengthsIn.
      for (const group of groups) {
        const name = group.name;
        const input = container.querySelector(`input[name="${CSS.escape(name)}"]`);
        if (!(input instanceof HTMLInputElement)) continue;
        const inches = this._sideLengthsIn[name];
        if (!Number.isFinite(inches)) continue;

        const display = toUnits === "cm" ? inchesToCm(inches) : inches;
        input.value = String(display);
        input.step = toUnits === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
      }

      // Edge distance input: keep internal inches, only change display/step.
      const edgeInput = container
        .closest(".est_configurator-container")
        ?.querySelector('input[name="cfg_edge_distance_in"]');
      if (edgeInput instanceof HTMLInputElement && Number.isFinite(this._edgeDistanceIn)) {
        const edgeDisplay = toUnits === "cm" ? inchesToCm(this._edgeDistanceIn) : this._edgeDistanceIn;
        edgeInput.value = String(edgeDisplay);
        edgeInput.step = toUnits === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
      }

      // Re-sync mins/constraints now that units changed.
      this._syncSideInputMinimums();
    }

    _cornerFlagsForSide(shape, label) {
      // Normative mapping from rules/05-calculations-for-parts-and-gaps.md
      // cornerAtStart/cornerAtEnd only when endpoint connects to another side (90° corner).
      switch (shape) {
        case "straight":
          return { cornerAtStart: false, cornerAtEnd: false };
        case "l_shape":
          if (label === "A") return { cornerAtStart: false, cornerAtEnd: true };
          if (label === "B") return { cornerAtStart: true, cornerAtEnd: false };
          return { cornerAtStart: false, cornerAtEnd: false };
        case "u_shape":
          if (label === "A") return { cornerAtStart: false, cornerAtEnd: true };
          if (label === "B") return { cornerAtStart: true, cornerAtEnd: true };
          if (label === "C") return { cornerAtStart: true, cornerAtEnd: false };
          return { cornerAtStart: false, cornerAtEnd: false };
        case "square":
          return { cornerAtStart: true, cornerAtEnd: true };
        case "open_3_corners":
          if (label === "A") return { cornerAtStart: false, cornerAtEnd: true };
          if (label === "B") return { cornerAtStart: true, cornerAtEnd: true };
          if (label === "C") return { cornerAtStart: true, cornerAtEnd: true };
          if (label === "D") return { cornerAtStart: true, cornerAtEnd: false };
          return { cornerAtStart: false, cornerAtEnd: false };
        default:
          return { cornerAtStart: false, cornerAtEnd: false };
      }
    }

    _computeEffectiveLengthIn({ shape, label, rawLengthIn, edgeDistanceIn }) {
      if (!Number.isFinite(rawLengthIn)) return NaN;
      const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
      const { cornerAtStart, cornerAtEnd } = this._cornerFlagsForSide(shape, label);
      const effective = rawLengthIn - (cornerAtStart ? edge : 0) - (cornerAtEnd ? edge : 0);
      return effective;
    }

    _syncEffectiveLengths() {
      const shape = this._getValue("cfg_shape");
      const edgeDistanceIn = this._edgeDistanceIn;
      const labels = this._getSideLabelsForShape(shape);
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";

      for (const label of labels) {
        // For square shapes with combined sides, skip C and D since they share readouts with A and B
        if (shape === "square" && (label === "C" || label === "D")) {
          continue;
        }

        const raw = this._getSideLengthInches(label);
        const eff = this._computeEffectiveLengthIn({
          shape,
          label,
          rawLengthIn: raw,
          edgeDistanceIn,
        });

        const out = this.querySelector(`[data-rb-side-effective="${CSS.escape(label)}"]`);
        if (!(out instanceof HTMLElement)) continue;

        if (!Number.isFinite(raw) || !Number.isFinite(edgeDistanceIn)) {
          out.textContent = "";
          continue;
        }

        const { cornerAtStart, cornerAtEnd } = this._cornerFlagsForSide(shape, label);
        const deductions = (cornerAtStart ? edgeDistanceIn : 0) + (cornerAtEnd ? edgeDistanceIn : 0);
        if (units === "cm") {
          const effCm = inchesToCm(eff);
          const dedCm = inchesToCm(deductions);
          // Localize unit suffix via translations: reuse effectiveLength string but strip fixed "po"/"in"
          // and append our own unit token.
          const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);
          const template = isFr
            ? "Longueur effective (centreligne) = {0} cm (déduction: {1} cm)"
            : "Effective Length (centerline) = {0} cm (deduction: {1} cm)";
          out.textContent = template
            .replace("{0}", effCm.toFixed(2))
            .replace("{1}", dedCm.toFixed(2));
        } else {
          out.textContent = t('effectiveLength', eff.toFixed(2), deductions.toFixed(2));
        }
      }
    }

    _getSideEffectiveLengthInches(label) {
      const shape = this._getValue("cfg_shape");
      const edgeDistanceIn = this._edgeDistanceIn;
      const raw = this._getSideLengthInches(label);
      return this._computeEffectiveLengthIn({
        shape,
        label,
        rawLengthIn: raw,
        edgeDistanceIn,
      });
    }

    _getSideLengthInches(label) {
      const shape = this._getValue("cfg_shape");
      let name = "";
      if (shape === "square") {
        name = label === "A" || label === "C" ? "cfg_side_AC_in" : "cfg_side_BD_in";
      } else {
        name = `cfg_side_${label}_in`;
      }
      const inches = this._sideLengthsIn[name];
      return Number.isFinite(inches) ? inches : NaN;
    }

    _renderOpenings() {
      const container = this._els.openingsContainer;
      if (!container) return;

      const shape = this._getValue("cfg_shape");
      const sideLabels = this._getSideLabelsForShape(shape);

      const rows = Array.from(container.querySelectorAll("[data-rb-opening-row]"));
      
      // Renumber all legends sequentially (1, 2, 3, ...)
      rows.forEach((row, index) => {
        const legend = row.querySelector("[data-rb-opening-legend]");
        if (legend) {
          legend.textContent = `${t('opening')} ${index + 1}`;
        }
      });
      
      for (const row of rows) {
        const sideSelect = row.querySelector('select[name^="opening_side_"]');
        if (sideSelect instanceof HTMLSelectElement) {
          const current = sideSelect.value;
          const idx = this._extractOpeningIndex(row);
          const sidesUsedByOtherRows = new Set();
          for (const r of rows) {
            if (r === row) continue;
            const otherIdx = this._extractOpeningIndex(r);
            if (!otherIdx) continue;
            const otherSide = this._getOpeningFieldValue(otherIdx, "side");
            if (otherSide) sidesUsedByOtherRows.add(otherSide);
          }
          const availableSides = sideLabels.filter(
            (s) => !sidesUsedByOtherRows.has(s) || s === current
          );
          sideSelect.innerHTML = "";
          const blank = document.createElement("option");
          blank.value = "";
          blank.textContent = t('choose');
          sideSelect.appendChild(blank);
          for (const s of availableSides) {
            const opt = document.createElement("option");
            opt.value = s;
            opt.textContent = s;
            sideSelect.appendChild(opt);
          }
          if (availableSides.includes(current)) sideSelect.value = current;
          else if (availableSides.length) sideSelect.value = "";
        }

        this._syncOpeningRowLabels(row);
        this._syncOpeningRowDefaults(row);
        this._syncOpeningRowInputs(row);
        this._syncOpeningRowComputed(row);
      }

      // Disable add button if no shape or all sides already have an opening
      const step = this.querySelector('section[data-rb-step="cfg_openings"]');
      if (step) {
        const addBtn = step.querySelector('[data-rb-action="add_opening"]');
        if (addBtn instanceof HTMLButtonElement) {
          const usedSidesCount = new Set(
            rows
              .map((r) => this._getOpeningFieldValue(this._extractOpeningIndex(r), "side"))
              .filter(Boolean)
          ).size;
          addBtn.disabled = sideLabels.length === 0 || usedSidesCount >= sideLabels.length;
        }

        // Move last row's remove link into the shared actions row (left slot)
        const actionsLeft = step.querySelector("[data-rb-openings-actions-left]");
        if (actionsLeft) {
          // First, return any button currently in actionsLeft to its owner row
          const currentBtn = actionsLeft.querySelector("[data-rb-remove-opening]");
          if (currentBtn) {
            // Use the stored row reference if available, otherwise find by index
            const ownerRow = currentBtn._ownerRow || (() => {
              const idxAttr = currentBtn.getAttribute("data-rb-opening-index");
              if (!idxAttr) return null;
              return rows.find((r) => String(this._extractOpeningIndex(r)) === idxAttr);
            })();
            
            if (ownerRow && rows.includes(ownerRow) && ownerRow.isConnected) {
              const ownerRemoveSlot = ownerRow.querySelector(".cfg_openings-row-remove");
              if (ownerRemoveSlot && currentBtn.isConnected) {
                // Remove any existing button in the slot (if different) without clearing innerHTML
                const existingBtn = ownerRemoveSlot.querySelector("[data-rb-remove-opening]");
                if (existingBtn && existingBtn !== currentBtn) {
                  existingBtn.remove();
                }
                // Only move if not already in the correct place
                if (currentBtn.parentElement !== ownerRemoveSlot) {
                  ownerRemoveSlot.appendChild(currentBtn);
                }
              }
            } else {
              // Owner row doesn't exist anymore (was deleted), remove the orphaned button
              if (currentBtn.isConnected) {
                currentBtn.remove();
              }
            }
          }
          
          // Now move the last row's button to actionsLeft
          const lastRow = rows[rows.length - 1];
          if (lastRow) {
            const removeBtn = lastRow.querySelector("[data-rb-remove-opening]");
            if (removeBtn) {
              // Store reference to the row on the button for reliable tracking
              removeBtn._ownerRow = lastRow;
              
              // Only move if not already in actionsLeft
              if (removeBtn.parentElement !== actionsLeft && removeBtn.isConnected) {
                // Return any existing button in actionsLeft to its owner row first
                const existingBtn = actionsLeft.querySelector("[data-rb-remove-opening]");
                if (existingBtn && existingBtn !== removeBtn && existingBtn.isConnected) {
                  const existingOwnerRow = existingBtn._ownerRow;
                  if (existingOwnerRow && rows.includes(existingOwnerRow) && existingOwnerRow.isConnected) {
                    const existingOwnerSlot = existingOwnerRow.querySelector(".cfg_openings-row-remove");
                    if (existingOwnerSlot) {
                      existingOwnerSlot.appendChild(existingBtn);
                    }
                  } else {
                    // Owner row doesn't exist, remove the orphaned button
                    existingBtn.remove();
                  }
                }
                actionsLeft.appendChild(removeBtn);
              }
            } else {
              // No button found in last row, clear actionsLeft (but return any existing button first)
              const existingBtn = actionsLeft.querySelector("[data-rb-remove-opening]");
              if (existingBtn && existingBtn.isConnected) {
                const existingOwnerRow = existingBtn._ownerRow;
                if (existingOwnerRow && rows.includes(existingOwnerRow) && existingOwnerRow.isConnected) {
                  const existingOwnerSlot = existingOwnerRow.querySelector(".cfg_openings-row-remove");
                  if (existingOwnerSlot) {
                    existingOwnerSlot.appendChild(existingBtn);
                  }
                } else {
                  existingBtn.remove();
                }
              }
              // Clear any remaining content
              while (actionsLeft.firstChild) {
                actionsLeft.removeChild(actionsLeft.firstChild);
              }
            }
          } else {
            // No rows, clear actionsLeft
            actionsLeft.innerHTML = "";
          }
        }
      }
    }

    _validateOpenings() {
      const container = this._els.openingsContainer;
      if (!container) return true;

      const rows = Array.from(container.querySelectorAll("[data-rb-opening-row]"));
      const usedSides = new Map();

      for (const row of rows) {
        const idx = this._extractOpeningIndex(row);
        if (!idx) continue;

        const side = this._getOpeningFieldValue(idx, "side");
        if (side) usedSides.set(side, (usedSides.get(side) || 0) + 1);
      }

      for (const row of rows) {
        const idx = this._extractOpeningIndex(row);
        if (!idx) continue;

        const side = this._getOpeningFieldValue(idx, "side");
        const type = this._getOpeningFieldValue(idx, "type");
        const x = type === "door"
          ? parseNumber(this._getOpeningFieldValue(idx, "x"))
          : this._displayToInches(this._getOpeningFieldValue(idx, "x"));
        const a1 = this._displayToInches(this._getOpeningFieldValue(idx, "a1"));

        if (side && (usedSides.get(side) || 0) > 1) {
          window.alert(t('oneOpeningPerSide', side));
          const sideSelect = row.querySelector(`select[name="opening_side_${CSS.escape(idx)}"]`);
          if (sideSelect instanceof HTMLElement) sideSelect.focus();
          return false;
        }

        const sideLen = side ? this._getSideLengthInches(side) : NaN;
        if (Number.isFinite(sideLen) && Number.isFinite(x) && Number.isFinite(a1)) {
          const a2 = sideLen - x - a1;
          if (a2 < 0) {
            window.alert(t('negativeSegment', 'A2', 'A1'));
            const xInput = row.querySelector(`input[name="opening_x_${CSS.escape(idx)}"]`);
            if (xInput instanceof HTMLElement) xInput.focus();
            return false;
          }

          const shape = this._getValue("cfg_shape");
          const edgeDistanceIn = this._edgeDistanceIn;
          const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
          const { cornerAtStart, cornerAtEnd } = side ? this._cornerFlagsForSide(shape, side) : { cornerAtStart: false, cornerAtEnd: false };
          const a1Effective = a1 - (cornerAtStart ? edge : 0);
          const a2Effective = a2 - (cornerAtEnd ? edge : 0);
          if (a1Effective < 0 || a2Effective < 0) {
            window.alert(t('negativeEffective', 'A1/A2'));
            return false;
          }
        }
      }

      return true;
    }

    _addOpeningRow() {
      const container = this._els.openingsContainer;
      if (!container) return;

      const template = this.querySelector("#cfg-opening-legend-row-template");
      const existingRows = container.querySelectorAll("[data-rb-opening-row]").length;
      const idx = existingRows + 1;

      const row = document.createElement("fieldset");
      row.setAttribute("data-rb-opening-row", "");

      const removeContainer = template && template.content
        ? (() => {
            const fragment = template.content.cloneNode(true);
            const legend = fragment.querySelector("[data-rb-opening-legend]");
            if (legend) legend.textContent = `${t('opening')} ${idx}`;
            row.appendChild(fragment);
            const removeBtn = row.querySelector("[data-rb-remove-opening]");
            if (removeBtn) {
              removeBtn.setAttribute("data-rb-opening-index", String(idx));
              // Store reference to the row for reliable tracking
              removeBtn._ownerRow = row;
              removeBtn.addEventListener("click", () => {
                row.remove();
                this._renderOpenings();
                this._renderPreview();
                this._updateDynamicEstimate();
              });
            }
            return row.querySelector(".cfg_openings-row-remove");
          })()
        : null;

      if (!removeContainer) {
        const legend = document.createElement("legend");
        legend.textContent = `${t('opening')} ${idx}`;
        row.appendChild(legend);
        const fallbackRemove = document.createElement("div");
        fallbackRemove.className = "cfg_openings-row-remove";
        const removeBtn = document.createElement("button");
        removeBtn.type = "button";
        removeBtn.setAttribute("data-rb-remove-opening", "");
        removeBtn.setAttribute("data-rb-opening-index", String(idx));
        removeBtn.textContent = t('removeOpening');
        // Store reference to the row for reliable tracking
        removeBtn._ownerRow = row;
        removeBtn.addEventListener("click", () => {
          row.remove();
          this._renderOpenings();
          this._renderPreview();
          this._updateDynamicEstimate();
        });
        fallbackRemove.appendChild(removeBtn);
        row.appendChild(fallbackRemove);
      }

      const insertBefore = removeContainer
        ? (el) => row.insertBefore(el, removeContainer)
        : (el) => row.insertBefore(el, row.querySelector(".cfg_openings-row-remove"));

      const sideP = document.createElement("p");
      const sideLab = document.createElement("label");
      sideLab.textContent = `${t('side')} `;
      const sideSelect = document.createElement("select");
      sideSelect.name = `opening_side_${idx}`;
      sideSelect.required = true;
      sideLab.appendChild(sideSelect);
      sideP.appendChild(sideLab);
      insertBefore(sideP);

      const typeP = document.createElement("p");
      const typeLab = document.createElement("label");
      typeLab.textContent = `${t('type')} `;
      const typeSelect = document.createElement("select");
      typeSelect.name = `opening_type_${idx}`;
      typeSelect.required = true;
      typeSelect.innerHTML = `
        <option value="" selected>${t('choose')}</option>
        <option value="opening">${t('openingOrStairs')}</option>
        <option value="door">${t('door')}</option>
      `;
      typeLab.appendChild(typeSelect);
      typeP.appendChild(typeLab);
      insertBefore(typeP);

      const xP = document.createElement("p");
      xP.setAttribute("data-rb-opening-x", "");
      insertBefore(xP);

      const doorP = document.createElement("p");
      doorP.setAttribute("data-rb-opening-door", "");
      insertBefore(doorP);

      const detailInput = document.createElement("input");
      detailInput.type = "hidden";
      detailInput.name = `opening_detail_${idx}`;
      insertBefore(detailInput);

      const variantIdInput = document.createElement("input");
      variantIdInput.type = "hidden";
      variantIdInput.name = `opening_variant_id_${idx}`;
      insertBefore(variantIdInput);

      const a1P = document.createElement("p");
      a1P.setAttribute("data-rb-opening-a1", "");
      const a1Lab = document.createElement("label");
      const a1Text = document.createElement("span");
      a1Text.setAttribute("data-rb-opening-a1-label", "");
      a1Text.textContent = `${t('adjacentSegment')} `;
      a1Lab.appendChild(a1Text);
      const a1Input = document.createElement("input");
      a1Input.type = "number";
      a1Input.name = `opening_a1_${idx}`;
      a1Input.required = true;
      a1Input.min = "0";
      a1Input.step = "0.125";
      a1Input.inputMode = "decimal";
      a1Lab.appendChild(a1Input);
      a1P.appendChild(a1Lab);
      insertBefore(a1P);

      const derivedP = document.createElement("p");
      const derived = document.createElement("small");
      derived.setAttribute("data-rb-opening-derived", "");
      derived.textContent = "";
      derivedP.appendChild(derived);
      insertBefore(derivedP);

      const errorP = document.createElement("p");
      const error = document.createElement("small");
      error.setAttribute("data-rb-opening-error", "");
      errorP.appendChild(error);
      insertBefore(errorP);

      container.appendChild(row);
      this._renderOpenings();
    }

    _getHeightCode() {
      const h = this._getValue("cfg_height");
      if (h === "36") return "H34";
      if (h === "42") return "H40";
      if (h === "48") return "H46";
      return "";
    }

    _normalizeDoorDetailKey(value) {
      const v = String(value || "").toLowerCase().replace(/\s*\+\s*/g, "_").replace(/\s+/g, "_");
      if (
        v.includes("loquet_verrou_plancher") ||
        v.includes("locket_verrou_plancher") ||
        v.includes("latch_floor_lock") ||
        v.includes("latch+floor_lock") ||
        v.includes("floor_lock+latch")
      ) return "locket_verrou_plancher";
      if (v.includes("verrou_plancher") || v.includes("floor_lock")) return "verrou_plancher";
      if (v.includes("loquet") || v.includes("locket") || v.includes("latch")) return "locket";
      return v || "locket";
    }

    _getDoorOptions(heightCode) {
      const doorsAll = Array.isArray(this._catalog?.doors) ? this._catalog.doors : [];
      if (!heightCode) return [];
      const finish = this._getValue("cfg_finish");
      const wantBlack = finish === "matte_black";

      const doors = doorsAll.filter((v) => {
        const title = String(v?.product_title || v?.product_handle || "").toLowerCase();
        const isBlack = title.includes("noir") || title.includes("black");
        const isStainless =
          title.includes("inox") ||
          title.includes("stainless") ||
          title.includes("acier inox");
        if (wantBlack) return isBlack;
        // Stainless: prefer explicit stainless, but keep neutral entries too.
        return isStainless || !isBlack;
      });

      const seen = new Set();
      return doors
        .filter((v) => v && v.metafields && v.metafields.height_code === heightCode)
        .filter((v) => v.metafields && Number.isFinite(Number(v.metafields.length_in)))
        .map((v) => ({
          variantId: v.variant_id,
          lengthIn: Number(v.metafields.length_in),
          detail: v.metafields.detail || "",
          _key: `${heightCode}-${Number(v.metafields.length_in)}-${this._normalizeDoorDetailKey(v.metafields.detail)}`,
        }))
        .filter((opt) => {
          if (seen.has(opt._key)) return false;
          seen.add(opt._key);
          return true;
        })
        .map(({ variantId, lengthIn, detail }) => ({ variantId, lengthIn, detail }));
    }

    _getPanelLengths(heightCode, { perforated = false } = {}) {
      if (!heightCode) return [];
      const list = perforated ? this._catalog?.glassPerforated : this._catalog?.glassStandard;
      const panels = Array.isArray(list) ? list : [];
      return panels
        .filter((v) => v && v.metafields && v.metafields.height_code === heightCode)
        .map((v) => Number(v.metafields.length_in))
        .filter((v) => Number.isFinite(v) && v > 0);
    }

    _getMinPanelLength(heightCode, requirePerforated) {
      const standard = this._getPanelLengths(heightCode, { perforated: false });
      const perforated = this._getPanelLengths(heightCode, { perforated: true });
      const minStandard = standard.length ? Math.min(...standard) : NaN;
      const minPerforated = perforated.length ? Math.min(...perforated) : NaN;
      if (requirePerforated && Number.isFinite(minPerforated)) return minPerforated;
      if (Number.isFinite(minStandard)) return minStandard;
      return Number.isFinite(minPerforated) ? minPerforated : NaN;
    }

    _formatDoorDetail(detail) {
      const raw = String(detail || "");
      const v = raw.toLowerCase();
      const isFr = !!(window.CfgTranslations?.isFr);
      if (v.includes("loquet_verrou_plancher") || v.includes("locket_verrou_plancher")) {
        return isFr ? "Loquet + Verrou plancher" : "Latch + Floor Lock";
      }
      if (v.includes("verrou_plancher")) {
        return isFr ? "Verrou plancher" : "Floor Lock";
      }
      if (v.includes("loquet") || v.includes("locket")) {
        return isFr ? "Loquet" : "Latch";
      }
      return raw || (isFr ? "Porte" : "Door");
    }

    _syncOpeningRowInputs(row) {
      const idx = this._extractOpeningIndex(row);
      if (!idx) return;
      const units = this._currentUnitsValue();

      const type = this._getOpeningFieldValue(idx, "type");
      const xWrap = row.querySelector("[data-rb-opening-x]");
      const a1Wrap = row.querySelector("[data-rb-opening-a1]");
      const doorWrap = row.querySelector("[data-rb-opening-door]");
      if (!(xWrap instanceof HTMLElement)) return;

      const currentX = this._getOpeningFieldValue(idx, "x");
      const currentVariantId = this._getOpeningFieldValue(idx, "variantId");
      const currentDetail = this._getOpeningFieldValue(idx, "detail");
      const currentHinge = this._getOpeningFieldValue(idx, "hinge");
      const currentSwing = this._getOpeningFieldValue(idx, "swing");
      const detailInput = row.querySelector(`input[name="opening_detail_${CSS.escape(idx)}"]`);
      const variantInput = row.querySelector(`input[name="opening_variant_id_${CSS.escape(idx)}"]`);

      const showDetails = !isEmptyString(type);
      xWrap.hidden = !showDetails;
      if (doorWrap instanceof HTMLElement) doorWrap.hidden = !(type === "door");
      if (a1Wrap instanceof HTMLElement) a1Wrap.hidden = !showDetails;

      xWrap.innerHTML = "";

      const xLab = document.createElement("label");

      if (type === "door") {
        xLab.textContent = `${this._unitizeLabel(t('doorLength'))} `;
        const select = document.createElement("select");
        select.name = `opening_x_choice_${idx}`;
        select.required = true;
        select.innerHTML = `<option value="" selected>${t('choose')}</option>`;

        const heightCode = this._getHeightCode();
        const options = this._getDoorOptions(heightCode);
        for (const opt of options) {
          const o = document.createElement("option");
          o.value = String(opt.variantId || "");
          o.textContent = `${opt.lengthIn}" — ${this._formatDoorDetail(opt.detail)}`;
          o.dataset.detail = opt.detail;
          o.dataset.variantId = String(opt.variantId || "");
          o.dataset.lengthIn = String(opt.lengthIn);
          select.appendChild(o);
        }

        if (currentVariantId) select.value = String(currentVariantId);
        if (!select.value || select.value === "") {
          const opts = Array.from(select.options);
          const currentXNum = parseNumber(currentX);
          const currentDetailKey = this._normalizeDoorDetailKey(currentDetail);
          let match = null;
          if (Number.isFinite(currentXNum) && currentDetail) {
            match = opts.find(
              (opt) =>
                opt.dataset &&
                Number(opt.dataset.lengthIn) === currentXNum &&
                this._normalizeDoorDetailKey(opt.dataset.detail) === currentDetailKey
            );
          }
          if (!match && Number.isFinite(currentXNum)) {
            match = opts.find((opt) => opt.dataset && Number(opt.dataset.lengthIn) === currentXNum);
          }
          if (match) select.value = match.value;
        }

        const hiddenX = document.createElement("input");
        hiddenX.type = "hidden";
        hiddenX.name = `opening_x_${idx}`;
        if (currentX) hiddenX.value = currentX;

        const selected = select.selectedOptions[0];
        if (detailInput instanceof HTMLInputElement) detailInput.value = selected?.dataset?.detail || "";
        if (variantInput instanceof HTMLInputElement) variantInput.value = selected?.dataset?.variantId || "";
        if (selected?.dataset?.lengthIn) hiddenX.value = selected.dataset.lengthIn;

        select.addEventListener("change", () => {
          const selected = select.selectedOptions[0];
          if (detailInput instanceof HTMLInputElement) detailInput.value = selected?.dataset?.detail || "";
          if (variantInput instanceof HTMLInputElement) variantInput.value = selected?.dataset?.variantId || "";
          if (selected?.dataset?.lengthIn) hiddenX.value = selected.dataset.lengthIn;
        });

        xLab.appendChild(select);
        xLab.appendChild(hiddenX);
      } else {
        xLab.textContent = `${this._unitizeLabel(t('openingLength'))} `;
        const xInput = document.createElement("input");
        xInput.type = "number";
        xInput.name = `opening_x_${idx}`;
        xInput.required = true;
        xInput.min = "0";
        xInput.step = units === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
        xInput.inputMode = "decimal";
        if (currentX) xInput.value = currentX;
        xLab.appendChild(xInput);

        if (detailInput instanceof HTMLInputElement) detailInput.value = "";
        if (variantInput instanceof HTMLInputElement) variantInput.value = "";
      }

      xWrap.appendChild(xLab);

      if (doorWrap instanceof HTMLElement) {
        doorWrap.innerHTML = "";
        if (type === "door") {
          const side = this._getOpeningFieldValue(idx, "side");
          const sideLetter = side || "A";

          const hingeLab = document.createElement("label");
          hingeLab.textContent = `${t('hingeSide')} `;
          const hingeSelect = document.createElement("select");
          hingeSelect.name = `opening_hinge_${idx}`;
          hingeSelect.required = true;
          hingeSelect.innerHTML = `
            <option value="" selected>${t('choose')}</option>
            <option value="${sideLetter}1">${sideLetter}1</option>
            <option value="${sideLetter}2">${sideLetter}2</option>
          `;
          hingeLab.appendChild(hingeSelect);

          const swingLab = document.createElement("label");
          swingLab.textContent = `${t('openingDirection')} `;
          const swingSelect = document.createElement("select");
          swingSelect.name = `opening_swing_${idx}`;
          swingSelect.required = true;
          swingSelect.innerHTML = `
            <option value="" selected>${t('choose')}</option>
            <option value="inside">${t('inside')}</option>
            <option value="outside">${t('outside')}</option>
          `;
          swingLab.appendChild(swingSelect);

          const hingeP = document.createElement("p");
          hingeP.appendChild(hingeLab);
          const swingP = document.createElement("p");
          swingP.appendChild(swingLab);

          doorWrap.appendChild(hingeP);
          doorWrap.appendChild(swingP);

          if (currentHinge === `${sideLetter}1` || currentHinge === `${sideLetter}2`) {
            hingeSelect.value = currentHinge;
          }
          if (currentSwing === "inside" || currentSwing === "outside") {
            swingSelect.value = currentSwing;
          }
        }
      }
    }

    _syncOpeningRowLabels(row) {
      const idx = this._extractOpeningIndex(row);
      if (!idx) return;
      const side = this._getOpeningFieldValue(idx, "side");
      const sideLetter = side || "A";
      const a1Label = row.querySelector("[data-rb-opening-a1-label]");
      const hingeSelect = row.querySelector(`select[name="opening_hinge_${CSS.escape(idx)}"]`);
      if (a1Label instanceof HTMLElement) {
        a1Label.textContent = `${this._unitizeLabel(t('adjacentSegment').replace('A1', `${sideLetter}1`))} `;
      }
      if (hingeSelect instanceof HTMLSelectElement) {
        const current = hingeSelect.value;
        hingeSelect.innerHTML = `
          <option value="" selected>${t('choose')}</option>
          <option value="${sideLetter}1">${sideLetter}1</option>
          <option value="${sideLetter}2">${sideLetter}2</option>
        `;
        if (current === `${sideLetter}1` || current === `${sideLetter}2`) {
          hingeSelect.value = current;
        }
      }
    }

    _syncOpeningRowDefaults(row) {
      const idx = this._extractOpeningIndex(row);
      if (!idx) return;
      const side = this._getOpeningFieldValue(idx, "side");
      if (!side) return;
      const a1Input = row.querySelector(`input[name="opening_a1_${CSS.escape(idx)}"]`);
      if (!(a1Input instanceof HTMLInputElement)) return;

      const shape = this._getValue("cfg_shape");
      const edgeDistanceIn = this._edgeDistanceIn;
      const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
      const { cornerAtStart } = this._cornerFlagsForSide(shape, side);
      const defaultA1 = cornerAtStart ? edge : 0;
      a1Input.min = String(this._inchesToDisplay(defaultA1));
      const current = this._displayToInches(a1Input.value);
      if (!Number.isFinite(current) || current < defaultA1) {
        a1Input.value = String(this._inchesToDisplay(defaultA1));
      }
    }

    _syncOpeningRowComputed(row, options = {}) {
      const idx = this._extractOpeningIndex(row);
      if (!idx) return;

      const side = this._getOpeningFieldValue(idx, "side");
      const type = this._getOpeningFieldValue(idx, "type");
      const isDoor = type === "door";
      let x = isDoor
        ? parseNumber(this._getOpeningFieldValue(idx, "x"))
        : this._displayToInches(this._getOpeningFieldValue(idx, "x"));
      let a1 = this._displayToInches(this._getOpeningFieldValue(idx, "a1"));
      const hinge = this._getOpeningFieldValue(idx, "hinge");
      const a1Input = row.querySelector(`input[name="opening_a1_${CSS.escape(idx)}"]`);
      const xInput = row.querySelector(`input[name="opening_x_${CSS.escape(idx)}"]`);
      const prevA1Raw = this._displayToInches(a1Input?.dataset?.prevValue);
      const hasPrevA1 = Number.isFinite(prevA1Raw);
      const isLiveA1Edit =
        !!options.isLiveInput &&
        options.sourceInput instanceof HTMLInputElement &&
        a1Input instanceof HTMLInputElement &&
        options.sourceInput === a1Input;

      const derivedOut = row.querySelector("[data-rb-opening-derived]");
      const errorEl = row.querySelector("[data-rb-opening-error]");

      const sideLen = side ? this._getSideLengthInches(side) : NaN;
      const shape = this._getValue("cfg_shape");
      const edgeDistanceIn = this._edgeDistanceIn;
      const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
      const { cornerAtStart, cornerAtEnd } = side ? this._cornerFlagsForSide(shape, side) : { cornerAtStart: false, cornerAtEnd: false };
      const heightCode = this._getHeightCode();

      const hingeGapToPerforated = 0.25;
      const hingeGapToWallOrPoteau = 1;
      const latchGap = 0.625;
      const hingeOnA1 = String(hinge || "").endsWith("1");
      const a1Raw = this._displayToInches(this._getOpeningFieldValue(idx, "a1"));
      const a2Raw = this._displayToInches(this._getOpeningFieldValue(idx, "a2"));
      const a1Eff = Number.isFinite(a1Raw) ? a1Raw - (cornerAtStart ? edge : 0) : NaN;
      const a2Eff = Number.isFinite(a2Raw) ? a2Raw - (cornerAtEnd ? edge : 0) : NaN;
      const eps = 1e-6;
      const doorWidth = isDoor && Number.isFinite(x) ? Number(x) : NaN;
      const DoorKit = typeof window !== "undefined" ? window.CfgDoorKit : null;
      const hingeSegHasLen = hingeOnA1 ? (Number.isFinite(a1Eff) && a1Eff > eps) : (Number.isFinite(a2Eff) && a2Eff > eps);
      const hingeGap = hingeSegHasLen ? hingeGapToPerforated : hingeGapToWallOrPoteau;
      const gapAtA1Door = hingeOnA1 ? hingeGap : latchGap;
      const gapAtA2Door = hingeOnA1 ? latchGap : hingeGap;
      const cornerGapMin = -0.25;
      const wallGapMin = 0.75;
      const allowZeroA1 = isDoor && !cornerAtStart;
      const allowZeroA2 = isDoor && !cornerAtEnd;

      const minPanelA1 = this._getMinPanelLength(heightCode, false);
      const minPanelA2 = this._getMinPanelLength(heightCode, false);

      // Door hinge non-zero min effective is the door width; latch still uses panel min + gaps.
      const startGapA1Min =
        cornerAtStart ? (isDoor && hingeOnA1 ? wallGapMin : cornerGapMin) : wallGapMin;
      const endGapA1Min = isDoor ? gapAtA1Door : 0;
      const startGapA2Min = isDoor ? gapAtA2Door : 0;
      const endGapA2Min =
        cornerAtEnd ? (isDoor && !hingeOnA1 ? wallGapMin : cornerGapMin) : wallGapMin;

      const minEffA1 = Number.isFinite(minPanelA1) ? minPanelA1 + startGapA1Min + endGapA1Min : NaN;
      const minEffA2 = Number.isFinite(minPanelA2) ? minPanelA2 + startGapA2Min + endGapA2Min : NaN;
      const hingeMinEff = Number.isFinite(doorWidth) && doorWidth > 0 ? doorWidth : NaN;
      const minEffA1Hinge = isDoor && hingeOnA1 && Number.isFinite(hingeMinEff) ? hingeMinEff : minEffA1;
      const minEffA2Hinge = isDoor && !hingeOnA1 && Number.isFinite(hingeMinEff) ? hingeMinEff : minEffA2;
      const minRawA1Allowed = cornerAtStart ? edge : 0;
      const minRawA2Allowed = cornerAtEnd ? edge : 0;
      const latchGapIn = 0.625;
      const latchReserveA1 = isDoor && !hingeOnA1 && cornerAtStart ? latchGapIn : 0;
      const latchReserveA2 = isDoor && hingeOnA1 && cornerAtEnd ? latchGapIn : 0;
      const minRawA1NonZero = Number.isFinite(minEffA1Hinge) ? minRawA1Allowed + minEffA1Hinge : minRawA1Allowed;
      const minRawA2NonZero = Number.isFinite(minEffA2Hinge) ? minRawA2Allowed + minEffA2Hinge : minRawA2Allowed;
      const minRawA1ForX = (cornerAtStart || allowZeroA1 ? minRawA1Allowed : minRawA1NonZero) + latchReserveA1;
      const minRawA2ForX = (cornerAtEnd || allowZeroA2 ? minRawA2Allowed : minRawA2NonZero) + latchReserveA2;

      if (xInput instanceof HTMLInputElement) {
        if (Number.isFinite(sideLen)) {
          const maxX = Math.max(0, sideLen - minRawA1ForX - minRawA2ForX);
          xInput.max = String(isDoor ? maxX : this._inchesToDisplay(maxX));
          let currentX = isDoor ? parseNumber(xInput.value) : this._displayToInches(xInput.value);
          if (Number.isFinite(currentX) && currentX > maxX) {
            xInput.value = String(isDoor ? maxX : this._inchesToDisplay(maxX));
            currentX = maxX;
          }
          if (Number.isFinite(currentX)) x = currentX;
        } else {
          xInput.removeAttribute("max");
        }
      }
      const minRawA1WithLatchGap = minRawA1Allowed + latchReserveA1;
      if (a1Input instanceof HTMLInputElement) {
        if (Number.isFinite(sideLen) && Number.isFinite(x)) {
          const minA1 = cornerAtStart || allowZeroA1 ? (latchReserveA1 ? minRawA1WithLatchGap : minRawA1Allowed) : minRawA1NonZero;
          const maxA1 = Math.max(0, sideLen - x - minRawA2ForX);
          a1Input.min = String(this._inchesToDisplay(minA1));
          a1Input.max = String(this._inchesToDisplay(maxA1));
          let current = this._displayToInches(a1Input.value);
          if (!isLiveA1Edit && Number.isFinite(current) && current < minA1) {
            a1Input.value = String(this._inchesToDisplay(minA1));
            current = minA1;
          }
          if (!isLiveA1Edit && Number.isFinite(current) && current > maxA1) {
            a1Input.value = String(this._inchesToDisplay(maxA1));
            current = maxA1;
          }
          if (!isLiveA1Edit && !(isDoor && hingeOnA1) && hasPrevA1 && (cornerAtStart || allowZeroA1) && Number.isFinite(minEffA1)) {
            if (current > minRawA1WithLatchGap && current < minRawA1NonZero) {
              const movingUp = current > prevA1Raw;
              const movingDown = current < prevA1Raw;
              const target = movingUp ? minRawA1NonZero : movingDown ? minRawA1WithLatchGap : minRawA1NonZero;
              const snapped = Math.max(minA1, Math.min(maxA1, target));
              a1Input.value = String(this._inchesToDisplay(snapped));
              current = snapped;
            }
          }
          if (Number.isFinite(current)) a1 = current;
        } else {
          a1Input.removeAttribute("max");
        }
      }
      let canCompute = Number.isFinite(sideLen) && Number.isFinite(x) && Number.isFinite(a1);
      let a2 = canCompute ? sideLen - x - a1 : NaN;

      const minRawA2WithLatchGap = minRawA2Allowed + latchReserveA2;
      if (!isLiveA1Edit && !(isDoor && !hingeOnA1) && hasPrevA1 && canCompute && (cornerAtEnd || allowZeroA2) && Number.isFinite(minEffA2)) {
        if (a2 > minRawA2WithLatchGap && a2 < minRawA2NonZero) {
          const movingDown = a1 < prevA1Raw;
          const movingUp = a1 > prevA1Raw;
          const targetA1 = sideLen - x - (movingDown ? minRawA2NonZero : movingUp ? minRawA2WithLatchGap : minRawA2NonZero);
          if (Number.isFinite(targetA1)) {
            const minA1 = cornerAtStart || allowZeroA1 ? minRawA1Allowed : minRawA1NonZero;
            const maxA1 = Math.max(0, sideLen - x - minRawA2ForX);
            const snapped = Math.max(minA1, Math.min(maxA1, targetA1));
            if (a1Input instanceof HTMLInputElement) a1Input.value = String(this._inchesToDisplay(snapped));
            a1 = snapped;
            a2 = sideLen - x - a1;
          }
        }
      }

      canCompute = Number.isFinite(sideLen) && Number.isFinite(x) && Number.isFinite(a1);
      a2 = canCompute ? sideLen - x - a1 : NaN;

      if (!isLiveA1Edit && isDoor && DoorKit && Number.isFinite(doorWidth) && doorWidth > 0 && canCompute) {
        if (hingeOnA1) {
          const edgeDeduction = cornerAtStart ? edge : 0;
          const nextEff = a1 - edgeDeduction;
          const prevEff = hasPrevA1 ? prevA1Raw - edgeDeduction : nextEff;
          const maxEff = Math.max(0, sideLen - x - minRawA2ForX) - edgeDeduction;
          const a1Moved = hasPrevA1 && Math.abs(a1 - prevA1Raw) > eps;
          const snappedEff = a1Moved
            ? DoorKit.snapHingeEffective({ previousEff: prevEff, nextEff, doorWidth })
            : DoorKit.resolveHingeEffective({ hingeEff: nextEff, doorWidth, maxEff });
          let snappedRaw = Math.max(minRawA1Allowed, Math.min(sideLen - x - minRawA2ForX, snappedEff + edgeDeduction));
          let hingeEffAfter = snappedRaw - edgeDeduction;
          if (hingeEffAfter > 0 && hingeEffAfter < doorWidth) {
            hingeEffAfter = DoorKit.resolveHingeEffective({ hingeEff: hingeEffAfter, doorWidth, maxEff });
            snappedRaw = Math.max(minRawA1Allowed, Math.min(sideLen - x - minRawA2ForX, hingeEffAfter + edgeDeduction));
          }
          if (a1Input instanceof HTMLInputElement) a1Input.value = String(this._inchesToDisplay(snappedRaw));
          a1 = snappedRaw;
          a2 = sideLen - x - a1;
        } else {
          const edgeDeduction = cornerAtEnd ? edge : 0;
          const nextEff = a2 - edgeDeduction;
          const prevA2 = hasPrevA1 ? sideLen - x - prevA1Raw : a2;
          const prevEff = prevA2 - edgeDeduction;
          const maxEff = Math.max(0, sideLen - x - minRawA1ForX) - edgeDeduction;
          const a2Moved = hasPrevA1 && Math.abs(a1 - prevA1Raw) > eps;
          const snappedEff = a2Moved
            ? DoorKit.snapHingeEffective({ previousEff: prevEff, nextEff, doorWidth })
            : DoorKit.resolveHingeEffective({ hingeEff: nextEff, doorWidth, maxEff });
          const snappedA2 = snappedEff + edgeDeduction;
          const snappedA1 = sideLen - x - snappedA2;
          let clampedA1 = Math.max(minRawA1ForX, Math.min(sideLen - x - minRawA2ForX, snappedA1));
          let hingeEffAfter = sideLen - x - clampedA1 - edgeDeduction;
          if (hingeEffAfter > 0 && hingeEffAfter < doorWidth) {
            hingeEffAfter = DoorKit.resolveHingeEffective({ hingeEff: hingeEffAfter, doorWidth, maxEff });
            clampedA1 = Math.max(minRawA1ForX, Math.min(sideLen - x - minRawA2ForX, sideLen - x - (hingeEffAfter + edgeDeduction)));
          }
          if (a1Input instanceof HTMLInputElement) a1Input.value = String(this._inchesToDisplay(clampedA1));
          a1 = clampedA1;
          a2 = sideLen - x - a1;
        }
      }

      if (!isLiveA1Edit && a1Input instanceof HTMLInputElement && Number.isFinite(a1)) {
        a1Input.dataset.prevValue = String(this._inchesToDisplay(a1));
      }

      const a1Effective = Number.isFinite(a1) ? a1 - (cornerAtStart ? edge : 0) : NaN;
      const a2Effective = Number.isFinite(a2) ? a2 - (cornerAtEnd ? edge : 0) : NaN;

      const errors = [];
      if (side && !Number.isFinite(sideLen)) errors.push(t('invalidSideLength'));
      const seg1 = side ? `${side}1` : "A1";
      const seg2 = side ? `${side}2` : "A2";
      if (canCompute && a2 < 0) errors.push(t('negativeSegment', seg2, seg1));
      if (Number.isFinite(a1Effective) && a1Effective < 0) errors.push(t('negativeEffective', seg1));
      if (Number.isFinite(a2Effective) && a2Effective < 0) errors.push(t('negativeEffective', seg2));

      // Enforce "one opening per side" (V1)
      if (side) {
        const usedSides = new Map();
        const rows = Array.from(this._els.openingsContainer.querySelectorAll("[data-rb-opening-row]"));
        for (const r of rows) {
          const i = this._extractOpeningIndex(r);
          if (!i) continue;
          const s = this._getOpeningFieldValue(i, "side");
          if (!s) continue;
          usedSides.set(s, (usedSides.get(s) || 0) + 1);
        }
        if ((usedSides.get(side) || 0) > 1) errors.push(t('oneOpeningPerSide', side));
      }

      if (derivedOut instanceof HTMLElement) {
        const sideEffectiveLen = side ? this._computeEffectiveLengthIn({ shape, label: side, rawLengthIn: sideLen, edgeDistanceIn }) : NaN;
        if (side && Number.isFinite(sideEffectiveLen) && Number.isFinite(a1Effective) && Number.isFinite(a2Effective) && Number.isFinite(edgeDistanceIn)) {
          const deductions = (cornerAtStart ? edgeDistanceIn : 0) + (cornerAtEnd ? edgeDistanceIn : 0);
          const units = this._currentUnitsValue();
          if (units === "cm") {
            const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);
            const sideEff = inchesToCm(sideEffectiveLen).toFixed(2);
            const ded = inchesToCm(deductions).toFixed(2);
            const s1 = inchesToCm(a1Effective).toFixed(2);
            const s2 = inchesToCm(a2Effective).toFixed(2);
            derivedOut.textContent = isFr
              ? `Longueur effective Côté ${side}: ${sideEff} cm (déduction: ${ded} cm). ${seg1}=${s1} cm, ${seg2}=${s2} cm.`
              : `Effective Length Side ${side}: ${sideEff} cm (deduction: ${ded} cm). ${seg1}=${s1} cm, ${seg2}=${s2} cm.`;
          } else {
            derivedOut.textContent = t('sideEffectiveLength', side, sideEffectiveLen.toFixed(2), deductions.toFixed(2), seg1, a1Effective.toFixed(2), seg2, a2Effective.toFixed(2));
          }
        } else {
          derivedOut.textContent = "";
        }
      }
      if (errorEl) errorEl.textContent = errors.join(" ");
    }

    _extractOpeningIndex(row) {
      const sideSelect = row.querySelector('select[name^="opening_side_"]');
      if (!(sideSelect instanceof HTMLSelectElement)) return null;
      const m = sideSelect.name.match(/^opening_side_(\d+)$/);
      return m ? m[1] : null;
    }

    _getOpeningFieldValue(idx, kind) {
      const nameMap = {
        side: `opening_side_${idx}`,
        type: `opening_type_${idx}`,
        x: `opening_x_${idx}`,
        a1: `opening_a1_${idx}`,
        detail: `opening_detail_${idx}`,
        variantId: `opening_variant_id_${idx}`,
        hinge: `opening_hinge_${idx}`,
        swing: `opening_swing_${idx}`,
      };
      const name = nameMap[kind];
      if (!name) return "";
      const el = this.querySelector(`[name="${CSS.escape(name)}"]`);
      return el && "value" in el ? el.value : "";
    }

    _renderStairs() {
      const container = this._els.stairsContainer;
      if (!container) return;
      const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";

      const rows = Array.from(container.querySelectorAll("[data-rb-stair-row]"));
      rows.forEach((row, index) => {
        const legend = row.querySelector("[data-rb-stair-legend]");
        if (legend) legend.textContent = `${t('stair')} ${index + 1}`;

        // Sync unit labels and step/min for stair numeric inputs
        const isFr = !!(window.CfgTranslations && window.CfgTranslations.isFr);
        const lengthLabel = row.querySelector('label:has([name^="stairs_length_in_"])');
        const heightLabel = row.querySelector('label:has([name^="stairs_height_in_"])');
        const diagLabel = row.querySelector('label:has([name^="stairs_diagonal_in_"])');
        const lengthInput = row.querySelector('input[name^="stairs_length_in_"]');
        const heightInput = row.querySelector('input[name^="stairs_height_in_"]');
        const diagInput = row.querySelector('input[name^="stairs_diagonal_in_"]');

        const updateLabel = (labelEl, baseFr, baseEn) => {
          if (!labelEl) return;
          labelEl.firstChild && labelEl.firstChild.nodeType === Node.TEXT_NODE
            ? (labelEl.firstChild.textContent =
                (isFr ? baseFr : baseEn) + (units === "cm" ? " (cm) " : isFr ? " (po) " : " (in) "))
            : labelEl.insertBefore(
                document.createTextNode(
                  (isFr ? baseFr : baseEn) + (units === "cm" ? " (cm) " : isFr ? " (po) " : " (in) ")
                ),
                labelEl.firstChild || null
              );
        };

        updateLabel(lengthLabel, "Longueur de l'escalier", "Stair length");
        updateLabel(heightLabel, "Hauteur de l'escalier", "Stair height");
        updateLabel(diagLabel, "Diagonale de l'escalier", "Stair diagonal");

        const syncInputUnits = (input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.step = units === "cm" ? String(INCH_STEP * CM_PER_INCH) : "0.125";
          input.min = "0";
        };

        syncInputUnits(lengthInput);
        syncInputUnits(heightInput);
        syncInputUnits(diagInput);
      });

      const step = this.querySelector('section[data-rb-step="cfg_stairs"]');
      if (step) {
        const actionsLeft = step.querySelector("[data-rb-stairs-actions-left]");
        if (actionsLeft) {
          const currentBtn = actionsLeft.querySelector("[data-rb-remove-stair]");
          if (currentBtn && currentBtn._ownerRow && rows.includes(currentBtn._ownerRow) && currentBtn._ownerRow.isConnected) {
            const ownerRemoveSlot = currentBtn._ownerRow.querySelector(".cfg_stairs-row-remove");
            if (ownerRemoveSlot) {
              ownerRemoveSlot.appendChild(currentBtn);
            }
          } else if (currentBtn && currentBtn.isConnected) {
            currentBtn.remove();
          }
          const lastRow = rows[rows.length - 1];
          if (lastRow) {
            const removeBtn = lastRow.querySelector("[data-rb-remove-stair]");
            if (removeBtn && removeBtn.parentElement !== actionsLeft) {
              removeBtn._ownerRow = lastRow;
              const existingBtn = actionsLeft.querySelector("[data-rb-remove-stair]");
              if (existingBtn && existingBtn !== removeBtn) existingBtn.remove();
              actionsLeft.appendChild(removeBtn);
            }
          } else {
            actionsLeft.innerHTML = "";
          }
        }
      }

      const totalEl = step ? step.querySelector("[data-rb-stairs-total]") : null;
      if (totalEl) {
        const stairsCents = this._getStairsEstimateCents(this._collectConfiguratorInput());
        if (stairsCents > 0) {
          const amount = this._formatSummaryPrice(stairsCents).replace(/\$\s*$/, "").trim();
          totalEl.textContent = (t("stairsEstTotal") || "Est. stairs total: {0} $").replace("{0}", amount);
          totalEl.hidden = false;
        } else {
          totalEl.hidden = true;
        }
      }
    }

    _addStairsRow() {
      const container = this._els.stairsContainer;
      if (!container) return;
      const template = this.querySelector("#cfg-stair-row-template");
      if (!template || !template.content) return;

      const existingRows = container.querySelectorAll("[data-rb-stair-row]").length;
      const idx = existingRows + 1;

      const row = template.content.cloneNode(true).querySelector("[data-rb-stair-row]");
      if (!row) return;

      const legend = row.querySelector("[data-rb-stair-legend]");
      if (legend) legend.textContent = `${t('stair')} ${idx}`;

      row.querySelectorAll("[name='stairs_steps_1']").forEach((el) => { el.name = `stairs_steps_${idx}`; });
      row.querySelectorAll("[name='stairs_type_1']").forEach((el) => { el.name = `stairs_type_${idx}`; });
      row.querySelectorAll("[name='stairs_length_in_1']").forEach((el) => { el.name = `stairs_length_in_${idx}`; });
      row.querySelectorAll("[name='stairs_height_in_1']").forEach((el) => { el.name = `stairs_height_in_${idx}`; });
      row.querySelectorAll("[name='stairs_diagonal_in_1']").forEach((el) => { el.name = `stairs_diagonal_in_${idx}`; });

      const removeBtn = row.querySelector("[data-rb-remove-stair]");
      if (removeBtn) {
        removeBtn._ownerRow = row;
        removeBtn.addEventListener("click", () => {
          row.remove();
          this._renderStairs();
          this._renderPreview();
          this._updateDynamicEstimate();
        });
      }

      container.appendChild(row);
      this._renderStairs();
    }

    _extractStairIndex(row) {
      const stepsInput = row.querySelector('input[name^="stairs_steps_"]');
      if (!(stepsInput instanceof HTMLInputElement)) return null;
      const m = stepsInput.name.match(/^stairs_steps_(\d+)$/);
      return m ? m[1] : null;
    }

    _getStairFieldValue(idx, kind) {
      const nameMap = {
        steps: `stairs_steps_${idx}`,
        type: `stairs_type_${idx}`,
        lengthIn: `stairs_length_in_${idx}`,
        heightIn: `stairs_height_in_${idx}`,
        diagonalIn: `stairs_diagonal_in_${idx}`,
      };
      const name = nameMap[kind];
      if (!name) return "";
      const el = this.querySelector(`[name="${CSS.escape(name)}"]`);
      if (kind === "type") {
        const radio = el && "type" in el && el.type === "radio" ? this.querySelector(`input[name="${CSS.escape(name)}"]:checked`) : null;
        return radio && "value" in radio ? radio.value : "simple";
      }
      return el && "value" in el ? el.value : "";
    }

    _syncEstimateConditionalFields() {
      const doorsYes = this._getValue("est_has_doors") === "yes";
      let stairsYes = this._getValue("est_has_stairs") === "yes";
      const railingType = this._getValue("est_railing_type");
      const isAluminum = railingType === "aluminum";
      const aluminumType = this._getValue("est_aluminum_type");
      const showStandardAluminumOptions = isAluminum && aluminumType !== "ornementale";
      const isOrnamental = isAluminum && aluminumType === "ornementale";

      const doorWrap = this.querySelector("[data-rb-door-count]");
      if (doorWrap) doorWrap.hidden = !doorsYes;
      const doorInput = this.querySelector('[name="est_doors_count"]');
      if (doorInput instanceof HTMLInputElement) {
        doorInput.disabled = !doorsYes;
        doorInput.required = doorsYes;
        if (!doorsYes) doorInput.value = "0";
        if (doorsYes && (doorInput.value === "" || doorInput.value === "0")) doorInput.value = "1";
      }

      const stairsFieldset = this.querySelector('[name="est_has_stairs"]')?.closest("fieldset");
      if (stairsFieldset instanceof HTMLElement) stairsFieldset.hidden = isOrnamental;

      if (isOrnamental) {
        // Force no stairs for ornamental aluminum
        this._setValue("est_has_stairs", "no");
        stairsYes = false;
      }

      const stairsWrap = this.querySelector("[data-rb-stairs-details]");
      if (stairsWrap) stairsWrap.hidden = !stairsYes;
      const stepsInput = this.querySelector('[name="est_stairs_steps"]');
      if (stepsInput instanceof HTMLInputElement) {
        stepsInput.disabled = !stairsYes;
        stepsInput.required = stairsYes;
        if (!stairsYes) stepsInput.value = "0";
        if (stairsYes && (stepsInput.value === "" || stepsInput.value === "0")) stepsInput.value = "1";
      }
      const typeInputs = this.querySelectorAll('[name="est_stairs_type"]');
      for (const el of typeInputs) {
        if (el instanceof HTMLInputElement) el.disabled = !stairsYes;
      }

      const hardwareWrap = this.querySelector("[data-rb-est-glass-hardware-color]");
      if (hardwareWrap) hardwareWrap.hidden = isAluminum;
      const finishInputs = this.querySelectorAll('[name="est_finish"]');
      for (const el of finishInputs) {
        if (el instanceof HTMLInputElement) {
          el.disabled = isAluminum;
          el.required = !isAluminum;
        }
      }

      const aluminumFieldsets = this.querySelectorAll("[data-rb-est-aluminum]");
      for (const el of aluminumFieldsets) el.hidden = !isAluminum;

      const aluminumColorWrap = this.querySelector("[data-rb-est-aluminum-railing-color]");
      if (aluminumColorWrap) aluminumColorWrap.hidden = !showStandardAluminumOptions;
      const aluminumSelect = this.querySelector('[name="est_railing_color"]');
      if (aluminumSelect instanceof HTMLSelectElement) {
        aluminumSelect.disabled = !showStandardAluminumOptions;
        aluminumSelect.required = showStandardAluminumOptions;
        if (!showStandardAluminumOptions) aluminumSelect.value = "";
      }

      const mainWrap = this.querySelector("[data-rb-est-main-courante]");
      if (mainWrap) mainWrap.hidden = !showStandardAluminumOptions;
      const mainSelect = this.querySelector('[name="est_main_courante"]');
      if (mainSelect instanceof HTMLSelectElement) {
        mainSelect.disabled = !showStandardAluminumOptions;
        mainSelect.required = showStandardAluminumOptions;
        if (!showStandardAluminumOptions) mainSelect.value = "";
      }

      // Height behavior: ornamental aluminum forces 48" and disables 36"/42"
      const heightRadios = this.querySelectorAll('input[name="est_height"]');
      for (const el of heightRadios) {
        if (!(el instanceof HTMLInputElement)) continue;
        if (isOrnamental) {
          const is48 = el.value === "48";
          el.disabled = !is48;
          if (is48) el.checked = true;
        } else {
          el.disabled = false;
        }
      }
    }

    _syncInstallationDependentUi(container = this) {
      const installationInput = container.querySelector('input[name="est_installation"]:checked, input[name="cfg_installation"]:checked');
      const installationValue = installationInput instanceof HTMLInputElement ? installationInput.value : "";

      const msg = container.querySelector("[data-rb-installation-msg]");
      if (msg instanceof HTMLElement) {
        msg.hidden = installationValue !== "yes";
      }

      const deliveryFieldset = container.querySelector("[data-rb-delivery-fieldset]");
      if (deliveryFieldset instanceof HTMLElement) {
        const showDelivery = installationValue === "no";
        deliveryFieldset.hidden = !showDelivery;
        deliveryFieldset.querySelectorAll("input").forEach((input) => {
          if (input instanceof HTMLInputElement) input.disabled = !showDelivery;
        });
      }
    }

    _syncEstimateResultActions() {
      const isAluminum = this._getValue("est_railing_type") === "aluminum";
      const btn = this.querySelector("[data-rb-use-configurator]");
      if (btn instanceof HTMLButtonElement) {
        btn.hidden = isAluminum;
        btn.disabled = isAluminum;
      }
      const estResultSection = this.querySelector('section[data-rb-step="est_result"]');
      if (estResultSection) {
        estResultSection.classList.toggle("est_result--design-only", isAluminum);
        const orEl = estResultSection.querySelector(".est_result-or");
        const configuratorPanel = estResultSection.querySelector(".est_result-panel--configurator");
        if (orEl) orEl.hidden = isAluminum;
        if (configuratorPanel) configuratorPanel.hidden = isAluminum;
      }
    }

    _openInfo(key) {
      const dlg = this.querySelector(`#info-${CSS.escape(key)}`);
      if (dlg && typeof dlg.showModal === "function") {
        dlg.showModal();
        return;
      }
      window.alert(t('information') || "Information");
    }

    _getRecaptchaToken(siteKey, action) {
      return new Promise((resolve) => {
        if (!siteKey || typeof window.grecaptcha === "undefined" || typeof window.grecaptcha.execute !== "function") {
          resolve("");
          return;
        }
        window.grecaptcha.ready(() => {
          window.grecaptcha.execute(siteKey, { action })
            .then((token) => resolve(token || ""))
            .catch(() => resolve(""));
        });
      });
    }

    async _sendEstInfoToFormspree() {
      const infoStep = this.querySelector('section[data-rb-step="est_info"]');
      const infoForm = infoStep ? infoStep.querySelector("form[data-rb-form]") : null;
      if (!(infoForm instanceof HTMLFormElement)) return;

      const endpoint = (infoForm.getAttribute("action") || "").trim();
      if (!endpoint) return;

      const qe = this._collectEstimateInput();
      const person = this._collectPersonalInfo();
      const details = this._getEstimateDetailLines(qe);

      const hasPriceCalc = window.RBEstimationPrice && typeof window.RBEstimationPrice.calculate === "function";
      const priceResult = hasPriceCalc ? window.RBEstimationPrice.calculate(qe) : null;
      const estimatedTotal = priceResult && Number.isFinite(priceResult.total)
        ? String(priceResult.total)
        : "";

      const signature = JSON.stringify({
        person,
        qe,
        details,
        estimatedTotal,
      });
      if (signature === this._lastEstInfoFormspreeSignature) return;

      const formData = new FormData(infoForm);
      const locale = window.CfgTranslations && window.CfgTranslations.locale ? window.CfgTranslations.locale : "";
      const fullName = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();

      formData.set("source", "estimate_wizard");
      formData.set("locale", locale);
      formData.set("prenom", person.firstName || "");
      formData.set("nom", person.lastName || "");
      formData.set("_subject", fullName ? `Estimation - ${fullName}` : "Estimation");
      if (person.email) {
        formData.set("email", person.email);
        formData.set("_replyto", person.email);
      }
      formData.set("est_railing_type", qe.railingType || "");
      formData.set("est_aluminum_type", qe.aluminumType || "");
      formData.set("est_main_courante", qe.mainHandrail || "");
      formData.set("est_height", Number.isFinite(qe.heightIn) ? String(qe.heightIn) : "");
      formData.set("est_total_length_ft", Number.isFinite(qe.totalLengthFt) ? String(qe.totalLengthFt) : "");
      formData.set("est_finish", qe.finish || "");
      formData.set("est_railing_color", qe.railingColor || "");
      formData.set("est_has_doors", qe.doors.hasDoors ? "yes" : "no");
      formData.set("est_doors_count", Number.isFinite(qe.doors.count) ? String(qe.doors.count) : "");
      formData.set("est_has_stairs", qe.stairs.hasStairs ? "yes" : "no");
      formData.set("est_stairs_steps", Number.isFinite(qe.stairs.steps) ? String(qe.stairs.steps) : "");
      formData.set("est_stairs_type", qe.stairs.type || "");
      formData.set("estimate_details", details.join(" | "));
      formData.set("estimate_total", estimatedTotal);
      // Append fields from the soumission step form (installation, delivery + attachments)
      const soumissionStep = this.querySelector('section[data-rb-step="est_soumission"]');
      const soumissionForm = soumissionStep ? soumissionStep.querySelector('form[data-rb-form]') : null;
      const installationInput = soumissionForm ? soumissionForm.querySelector('input[name="est_installation"]:checked') : null;
      if (installationInput) formData.set("installation", installationInput.value);
      const deliveryInput = soumissionForm ? soumissionForm.querySelector('input[name="est_delivery"]:checked') : null;
      if (deliveryInput && !deliveryInput.disabled) formData.set("delivery", deliveryInput.value);
      // Same Formspree key as contact/career so the email template {{# attachment }} works.
      formData.delete("attachment");
      formData.delete("est_attachments");
      const attachmentsInput = soumissionForm ? soumissionForm.querySelector('input[name="est_attachments"]') : null;
      if (attachmentsInput instanceof HTMLInputElement && attachmentsInput.files) {
        for (const file of attachmentsInput.files) {
          if (file && file.size > 0) {
            formData.append("attachment", file, file.name);
          }
        }
      }
      const otherProjectsInputs = soumissionForm ? soumissionForm.querySelectorAll('input[name="est_other_projects[]"]:checked') : [];
      const otherProjects = Array.from(otherProjectsInputs).map(el => el.value).join(", ");
      if (otherProjects) formData.set("other_projects", otherProjects);
      const additionalInfoInput = soumissionForm ? soumissionForm.querySelector('textarea[name="est_additional_info"]') : null;
      if (additionalInfoInput && additionalInfoInput.value.trim()) formData.set("additional_info", additionalInfoInput.value.trim());

      const siteKey = (infoForm.getAttribute("data-recaptcha-site-key") || "").trim();
      const recaptchaToken = await this._getRecaptchaToken(siteKey, "estimation_submit");
      if (recaptchaToken) {
        formData.set("g-recaptcha-response", recaptchaToken);
      }

      const response = await fetch(endpoint, {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (!response.ok) {
        throw new Error(`Formspree response ${response.status}`);
      }
      this._lastEstInfoFormspreeSignature = signature;
    }

    _collectEstimateInput() {
      const hasDoors = this._getValue("est_has_doors") === "yes";
      const hasStairs = this._getValue("est_has_stairs") === "yes";
      const railingType = this._getValue("est_railing_type");
      const isAluminum = railingType === "aluminum";
       const aluminumType = isAluminum ? this._getValue("est_aluminum_type") : "";
       const mainHandrail = isAluminum && aluminumType !== "ornementale" ? this._getValue("est_main_courante") : "";
      return {
        railingType,
        aluminumType,
        mainHandrail,
        heightIn: parseNumber(this._getValue("est_height")),
        totalLengthFt: parseNumber(this._getValue("est_total_length_ft")),
        finish: isAluminum ? "" : this._getValue("est_finish"),
        railingColor: isAluminum ? this._getValue("est_railing_color") : "",
        doors: {
          hasDoors,
          count: hasDoors ? parseNumber(this._getValue("est_doors_count")) : 0,
        },
        stairs: {
          hasStairs,
          steps: hasStairs ? parseNumber(this._getValue("est_stairs_steps")) : 0,
          type: hasStairs ? this._getValue("est_stairs_type") : "",
        },
      };
    }

    _collectPersonalInfo() {
      const firstName = this._getValue("person_first_name");
      const lastName = this._getValue("person_last_name");
      return {
        name: [firstName, lastName].filter(Boolean).join(" ").trim(),
        firstName,
        lastName,
        phone: this._getValue("person_phone"),
        email: this._getValue("person_email"),
        address: this._getValue("person_address"),
        city: this._getValue("person_city"),
        postalCode: this._getValue("person_postal_code"),
      };
    }

    _getEstimateDetailLines(qe) {
      const lines = [];
      const heightIn = Number.isFinite(qe.heightIn) ? qe.heightIn : "";
      if (heightIn !== "") lines.push(t('rampsOf', heightIn));
      const lengthFt = Number.isFinite(qe.totalLengthFt) ? qe.totalLengthFt : "";
      if (lengthFt !== "") lines.push(t('totalLength', lengthFt));
      if (qe.railingType === "aluminum" && qe.aluminumType) {
        let typeLabel = "";
        if (qe.aluminumType === "aluminum_baluster") typeLabel = t('aluminumTypeBaluster');
        else if (qe.aluminumType === "aluminum_glass_6mm") typeLabel = t('aluminumTypeGlass6mm');
        else if (qe.aluminumType === "ornementale") typeLabel = t('aluminumTypeOrnamental');
        if (typeLabel) lines.push(typeLabel);
        if (qe.mainHandrail) {
          const mainLabel = qe.mainHandrail === "double" ? t('mainHandrailDouble') : t('mainHandrailSimple');
          if (mainLabel) lines.push(mainLabel);
        }
      }
      const finishLabel = this._getEstimateFinishLabel(qe);
      if (finishLabel) lines.push(finishLabel);
      if (qe.doors.hasDoors && Number.isFinite(qe.doors.count) && qe.doors.count >= 1) {
        lines.push(qe.doors.count === 1 ? t('doorCount', qe.doors.count) : t('doorCountPlural', qe.doors.count));
      }
      if (qe.stairs.hasStairs && Number.isFinite(qe.stairs.steps) && qe.stairs.steps >= 1) {
        const typeLabel = qe.stairs.type === "double" ? t('stairsDouble') : t('stairsSimple');
        const plural = qe.stairs.steps > 1 ? t('stairsStepsPlural') : "";
        lines.push(t('stairsSteps', qe.stairs.steps, plural, typeLabel));
      }
      return lines;
    }

    _updateDynamicEstimate() {
      const box = this._els.dynamicEstimate;
      const priceEl = this._els.dynamicEstimatePrice;
      if (!box || !priceEl || !Engine || typeof Engine.computeRecommendedBom !== "function") return;

      // Only show for configurator steps cfg_dimensions .. cfg_stairs
      const stepId = this._getCurrentStepId() || "";
      const cfgNum = this._getCfgStepNumber(stepId);
      const isVisibleCfgStep = this._isConfiguratorStep(stepId) && typeof cfgNum === "number" && cfgNum >= 3 && cfgNum <= 5;
      if (!isVisibleCfgStep) {
        box.hidden = true;
        return;
      }

      const cfg = this._collectConfiguratorInput();
      const engineOut = Engine.computeRecommendedBom(cfg, this._catalog);
      const heightIn = cfg?.base && Number.isFinite(cfg.base.heightIn) ? cfg.base.heightIn : 42;
      const bom = Array.isArray(engineOut?.recommendedBom) ? engineOut.recommendedBom : [];

      let customPanelsCents = 0;
      for (const item of bom) {
        if (!item || !(Number(item.qty) > 0)) continue;
        const unitCents = this._getCustomPanelPriceCents(item, heightIn);
        if (unitCents != null) customPanelsCents += (Number(item.qty) || 0) * unitCents;
      }
      const bomTotalCents = (Number(engineOut?.pricing?.totalPrice) || 0) + customPanelsCents;
      const stairsTotalCents = this._getStairsEstimateCents(cfg);

      const totalCents = bomTotalCents + stairsTotalCents;
      if (!(totalCents > 0)) {
        priceEl.textContent = "—";
        box.hidden = false;
        return;
      }

      priceEl.textContent = this._formatSummaryPrice(totalCents);
      box.hidden = false;
    }

    _populateEstimateResult() {
      const qe = this._collectEstimateInput();
      const person = this._collectPersonalInfo();
      const lines = this._getEstimateDetailLines(qe);

      if (this._els.estDetails) {
        this._els.estDetails.innerHTML = "";
        for (const text of lines) {
          const li = document.createElement("li");
          li.textContent = text;
          this._els.estDetails.appendChild(li);
        }
      }
      if (this._els.estPrice) {
        const priceResult = window.RBEstimationPrice && window.RBEstimationPrice.calculate(qe);
        this._els.estPrice.textContent = priceResult ? priceResult.formatted : "";
      }
    }

    _populateSoumissionPanel() {
      const qe = this._collectEstimateInput();
      const lines = this._getEstimateDetailLines(qe);

      if (this._els.soumissionDetails) {
        this._els.soumissionDetails.innerHTML = "";
        for (const text of lines) {
          const li = document.createElement("li");
          li.textContent = text;
          this._els.soumissionDetails.appendChild(li);
        }
      }
      if (this._els.soumissionPrice) {
        const priceResult = window.RBEstimationPrice && window.RBEstimationPrice.calculate(qe);
        this._els.soumissionPrice.textContent = priceResult ? priceResult.formatted : "";
      }
    }

    _getEstimateFinishLabel(qe) {
      if (qe.railingType === "aluminum") {
        const color = (qe.railingColor || "").trim();
        if (!color) return "";
        const labels = {
          noir: t('hardware').replace('{0}', 'Black'),
          brun_commercial: t('hardware').replace('{0}', 'Commercial Brown'),
          blanc: t('hardware').replace('{0}', 'White'),
          gris_charbon: t('hardware').replace('{0}', 'Charcoal Gray'),
          sable: t('hardware').replace('{0}', 'Sand'),
        };
        const label = labels[color] || t('hardware').replace('{0}', color);
        return label;
      }
      const f = (qe.finish || "").trim();
      if (f === "stainless") return t('hardwareStainless');
      if (f === "matte_black") return t('hardwareMatteBlack');
      return f ? t('hardware').replace('{0}', f) : "";
    }

    _prefillFromProductId(productId) {
      const preset = ESTIMATE_PRODUCT_PRESETS && ESTIMATE_PRODUCT_PRESETS[String(productId)];
      if (!preset) return;
      if (preset.railingType) this._setValue("est_railing_type", preset.railingType);
      if (preset.aluminumType) this._setValue("est_aluminum_type", preset.aluminumType);
      this._syncEstimateConditionalFields();
      this._syncEstimateResultActions();
    }

    _prefillConfiguratorFromEstimate() {
      const qe = this._collectEstimateInput();
      if (Number.isFinite(qe.heightIn)) this._setValue("cfg_height", String(qe.heightIn));
      const finish = qe.railingType === "aluminum" ? "" : (qe.finish || "").trim();
      if (finish === "stainless" || finish === "matte_black") {
        this._setValue("cfg_finish", finish);
      } else {
        this._setValue("cfg_finish", "stainless");
      }
    }

    /** Serialize current configuration to URL query params. */
    _serializeConfigToParams() {
      const params = new URLSearchParams();
      const stepId = this._getCurrentStepId() || "";
      if (stepId) params.set("step", stepId);
      params.set("cfg_units", this._currentUnitsValue());

      const simpleNames = [
        "est_railing_type", "est_aluminum_type", "est_main_courante", "est_height", "est_total_length_ft",
        "est_finish", "est_railing_color", "est_has_doors", "est_doors_count", "est_has_stairs",
        "est_stairs_steps", "est_stairs_type",
        "cfg_shape", "cfg_height", "cfg_finish", "cfg_surface_type",
        "cfg_plan_validation", "cfg_installation",
        "person_first_name", "person_last_name", "person_phone", "person_email",
        "person_address", "person_city", "person_postal_code",
      ];
      for (const name of simpleNames) {
        const val = this._getValue(name);
        if (val != null && String(val).trim() !== "") params.set(name, String(val).trim());
      }

      // Always persist canonical inch values, regardless of current displayed unit.
      if (Number.isFinite(this._edgeDistanceIn)) {
        params.set("cfg_edge_distance_in", String(this._edgeDistanceIn));
      }
      const sideNames = ["cfg_side_A_in", "cfg_side_B_in", "cfg_side_C_in", "cfg_side_D_in", "cfg_side_AC_in", "cfg_side_BD_in"];
      for (const name of sideNames) {
        const inches = this._sideLengthsIn[name];
        if (Number.isFinite(inches)) params.set(name, String(inches));
      }

      const cfg = this._collectConfiguratorInput();
      if (cfg && Array.isArray(cfg.openings) && cfg.openings.length > 0) {
        const arr = cfg.openings.map((o) => {
          const sk = (o.side || "a").toLowerCase();
          return {
            side: o.side, type: o.type, detail: o.detail, variantId: o.variantId,
            hinge: o.hinge, swing: o.swing,
            x1In: o.x1In, a1In: o[`${sk}1In`],
          };
        });
        params.set("openings", JSON.stringify(arr));
      }
      if (cfg && Array.isArray(cfg.stairs) && cfg.stairs.length > 0) {
        const arr = cfg.stairs.map((s) => ({
          steps: s.steps, type: s.type,
          lengthIn: s.lengthIn, heightIn: s.heightIn, diagonalIn: s.diagonalIn,
        }));
        params.set("stairs", JSON.stringify(arr));
      }
      return params;
    }

    /** Restore configuration from URLSearchParams. */
    _restoreFromParams(params) {
      if (!(params instanceof URLSearchParams)) return;
      const step = params.get("step");
      const shape = params.get("cfg_shape");
      const unitsParam = params.get("cfg_units");
      const targetUnits = unitsParam === "cm" ? "cm" : "in";

      // Restore canonical numeric values in inches first.
      this._setValue("cfg_units", "in");
      this._currentUnits = "in";

      if (shape) {
        this._setValue("cfg_shape", shape);
        this._lastShape = shape;
      }

      // Restore simple scalar fields first (finish/units/sides/etc) so dependent
      // option lists (like door variants) are built from the correct context.
      const simpleNames = [
        "est_railing_type", "est_aluminum_type", "est_main_courante", "est_height", "est_total_length_ft",
        "est_finish", "est_railing_color", "est_has_doors", "est_doors_count", "est_has_stairs",
        "est_stairs_steps", "est_stairs_type",
        "cfg_height", "cfg_finish", "cfg_surface_type", "cfg_edge_distance_in",
        "cfg_side_A_in", "cfg_side_B_in", "cfg_side_C_in", "cfg_side_D_in", "cfg_side_AC_in", "cfg_side_BD_in",
        "cfg_plan_validation", "cfg_installation",
        "person_first_name", "person_last_name", "person_phone", "person_email",
        "person_address", "person_city", "person_postal_code",
      ];
      for (const name of simpleNames) {
        const val = params.get(name);
        if (val != null) this._setValue(name, val);
      }

      this._syncShapeDependentUi();
      this._renderSidesInputsWithOptions({ reset: false });

      // Side inputs are dynamic and may not exist when simpleNames are restored above.
      // Re-apply saved side values now that dimension inputs are rendered.
      const sideNames = ["cfg_side_A_in", "cfg_side_B_in", "cfg_side_C_in", "cfg_side_D_in", "cfg_side_AC_in", "cfg_side_BD_in"];
      for (const name of sideNames) {
        const val = params.get(name);
        if (val != null) this._setValue(name, val);
      }

      const openingsJson = params.get("openings");
      if (openingsJson) {
        try {
          const arr = JSON.parse(openingsJson);
          if (Array.isArray(arr) && arr.length > 0) {
            const container = this._els.openingsContainer;
            if (container) {
              container.innerHTML = "";
              for (let i = 0; i < arr.length; i++) {
                this._addOpeningRow();
              }
              arr.forEach((o, i) => {
                const idx = String(i + 1);
                const side = (o.side || "").trim();
                if (side) this._setValue(`opening_side_${idx}`, side);
                if (o.type) this._setValue(`opening_type_${idx}`, o.type);
                if (o.detail != null) this._setValue(`opening_detail_${idx}`, String(o.detail));
                if (o.variantId != null) this._setValue(`opening_variant_id_${idx}`, String(o.variantId));
                const x1 = o.x1In ?? o.x1;
                const a1 = o.a1In ?? o[`${(side || "a").toLowerCase()}1In`] ?? o.a1;
                if (x1 != null && Number.isFinite(Number(x1))) this._setValue(`opening_x_${idx}`, String(x1));
                if (a1 != null && Number.isFinite(Number(a1))) this._setValue(`opening_a1_${idx}`, String(a1));
              });
              const rows = container.querySelectorAll("[data-rb-opening-row]");
              for (const row of rows) {
                if (row instanceof HTMLElement) {
                  this._syncOpeningRowDefaults(row);
                  this._syncOpeningRowComputed(row);
                }
              }
              this._renderOpenings();
              arr.forEach((o, i) => {
                const idx = String(i + 1);
                if (o.detail != null) this._setValue(`opening_detail_${idx}`, String(o.detail));
                if (o.variantId != null) this._setValue(`opening_variant_id_${idx}`, String(o.variantId));
                if (o.hinge != null && o.hinge !== "") this._setValue(`opening_hinge_${idx}`, String(o.hinge));
                if (o.swing != null && o.swing !== "") this._setValue(`opening_swing_${idx}`, String(o.swing));
              });
              // Re-apply row input sync once variant/detail are restored to force the
              // matching door option (length + detail + variant) before summary/BOM build.
              const rowsAfterRender = container.querySelectorAll("[data-rb-opening-row]");
              for (const row of rowsAfterRender) {
                if (row instanceof HTMLElement) {
                  this._syncOpeningRowInputs(row);
                  this._syncOpeningRowComputed(row);
                }
              }
            }
          }
        } catch (_) { /* ignore */ }
      }

      const stairsJson = params.get("stairs");
      if (stairsJson) {
        try {
          const arr = JSON.parse(stairsJson);
          if (Array.isArray(arr) && arr.length > 0) {
            const container = this._els.stairsContainer;
            if (container) {
              container.innerHTML = "";
              for (let i = 0; i < arr.length; i++) {
                this._addStairsRow();
              }
              arr.forEach((s, i) => {
                const idx = String(i + 1);
                if (s.steps != null) this._setValue(`stairs_steps_${idx}`, String(s.steps));
                if (s.type) this._setValue(`stairs_type_${idx}`, s.type);
                if (s.lengthIn != null) this._setValue(`stairs_length_in_${idx}`, String(s.lengthIn));
                if (s.heightIn != null) this._setValue(`stairs_height_in_${idx}`, String(s.heightIn));
                if (s.diagonalIn != null) this._setValue(`stairs_diagonal_in_${idx}`, String(s.diagonalIn));
              });
              this._renderStairs();
            }
          }
        } catch (_) { /* ignore */ }
      }

      // Rebuild canonical inch values after param restore so BOM/summary can compute
      // immediately even when landing directly on cfg_summary.
      this._normalizeAllNumberInputs();
      this._seedSideLengthsFromInputs();

      // Finally, switch displayed unit if needed while keeping canonical inches intact.
      if (targetUnits === "cm") {
        this._convertDimensionInputsUnits({ from: "in", to: "cm" });
        this._convertOpeningsAndStairsDisplayedUnits({ from: "in", to: "cm" });
        this._setValue("cfg_units", "cm");
        this._currentUnits = "cm";
      } else {
        this._setValue("cfg_units", "in");
        this._currentUnits = "in";
      }

      this._syncEstimateConditionalFields();
      this._syncEstimateResultActions();
      this._syncEffectiveLengths();
      this._renderOpenings();
      this._renderStairs();
      this._renderPreview();
      this._updateDynamicEstimate();

      if (step && STEP_IDS.includes(step)) {
        this._goToStep(step, { replace: true });
      }
    }

    /** Build full URL with current config as query params. */
    _getConfigSaveUrl() {
      const params = this._serializeConfigToParams();
      const base = window.location.origin + window.location.pathname;
      const qs = params.toString();
      return qs ? `${base}?${qs}` : base;
    }

    _collectConfiguratorInput() {
      const shape = this._getValue("cfg_shape");
      const sideLabels = this._getSideLabelsForShape(shape);
      const edgeDistanceIn = this._edgeDistanceIn;
      const sides = sideLabels.map((label) => {
        // Use the unified getter so square (A&C / B&D) works correctly.
        const rawLengthIn = this._getSideLengthInches(label);
        const effectiveLengthIn = this._computeEffectiveLengthIn({
          shape,
          label,
          rawLengthIn,
          edgeDistanceIn,
        });
        return {
          label,
          rawLengthIn,
          effectiveLengthIn,
          cornerFlags: this._cornerFlagsForSide(shape, label),
        };
      });

      const openings = [];
      const container = this._els.openingsContainer;
      if (container) {
        const rows = Array.from(container.querySelectorAll("[data-rb-opening-row]"));
        for (const row of rows) {
          const idx = this._extractOpeningIndex(row);
          if (!idx) continue;
          const side = this._getOpeningFieldValue(idx, "side");
          if (!side) continue;
          const type = this._getOpeningFieldValue(idx, "type");
          const x1In = type === "door"
            ? parseNumber(this._getOpeningFieldValue(idx, "x"))
            : this._displayToInches(this._getOpeningFieldValue(idx, "x"));
          const a1In = this._displayToInches(this._getOpeningFieldValue(idx, "a1"));
          const detail = this._getOpeningFieldValue(idx, "detail");
          const variantId = this._getOpeningFieldValue(idx, "variantId");
          const hinge = this._getOpeningFieldValue(idx, "hinge");
          const swing = this._getOpeningFieldValue(idx, "swing");

          const sideRawLengthIn = side ? this._getSideLengthInches(side) : NaN;
          const a2In = Number.isFinite(sideRawLengthIn) && Number.isFinite(x1In) && Number.isFinite(a1In) ? sideRawLengthIn - x1In - a1In : NaN;

          const { cornerAtStart, cornerAtEnd } = side ? this._cornerFlagsForSide(shape, side) : { cornerAtStart: false, cornerAtEnd: false };
          const edge = Number.isFinite(edgeDistanceIn) ? edgeDistanceIn : 0;
          const a1EffectiveIn = Number.isFinite(a1In) ? a1In - (cornerAtStart ? edge : 0) : NaN;
          const a2EffectiveIn = Number.isFinite(a2In) ? a2In - (cornerAtEnd ? edge : 0) : NaN;
          const sideEffectiveLengthIn = side
            ? this._computeEffectiveLengthIn({ shape, label: side, rawLengthIn: sideRawLengthIn, edgeDistanceIn })
            : NaN;

          const sideKey = side ? side.toLowerCase() : "a";
          openings.push({
            side,
            type,
            detail,
            variantId,
            hinge,
            swing,
            x1In,
            [`${sideKey}1In`]: a1In,
            [`${sideKey}2In`]: a2In,
            [`${sideKey}1EffectiveIn`]: a1EffectiveIn,
            [`${sideKey}2EffectiveIn`]: a2EffectiveIn,
            sideRawLengthIn,
            sideEffectiveLengthIn,
            cornerFlags: { cornerAtStart, cornerAtEnd },
          });
        }
      }

      const stairs = [];
      const stairsContainer = this._els.stairsContainer;
      if (stairsContainer) {
        const units = this._getValue("cfg_units") === "cm" ? "cm" : "in";
        const stairRows = Array.from(stairsContainer.querySelectorAll("[data-rb-stair-row]"));
        for (const row of stairRows) {
          const idx = this._extractStairIndex(row);
          if (!idx) continue;
          const steps = parseNumber(this._getStairFieldValue(idx, "steps"));
          const type = this._getStairFieldValue(idx, "type") || "simple";
          let length = parseNumber(this._getStairFieldValue(idx, "lengthIn"));
          let height = parseNumber(this._getStairFieldValue(idx, "heightIn"));
          let diagonal = parseNumber(this._getStairFieldValue(idx, "diagonalIn"));
          if (units === "cm") {
            length = cmToInches(length);
            height = cmToInches(height);
            diagonal = cmToInches(diagonal);
          }
          stairs.push({
            steps: Number.isFinite(steps) && steps >= 1 ? steps : 1,
            type,
            lengthIn: Number.isFinite(length) ? length : undefined,
            heightIn: Number.isFinite(height) ? height : undefined,
            diagonalIn: Number.isFinite(diagonal) ? diagonal : undefined,
          });
        }
      }

      const rawFinish = this._getValue("cfg_finish");
      const finish = rawFinish && (rawFinish === "stainless" || rawFinish === "matte_black") ? rawFinish : "stainless";

      return {
        base: {
          heightIn: parseNumber(this._getValue("cfg_height")),
          finish,
          surfaceType: this._getValue("cfg_surface_type"),
        },
        shape,
        edgeDistanceIn,
        sides,
        openings,
        stairs,
        personalInfo: this._collectPersonalInfo(),
      };
    }

    _getSummaryVariantMap() {
      const map = new Map();
      const add = (v) => {
        const id = v?.variant_id ?? v?.variantId;
        if (id) map.set(Number(id), v);
      };
      const list = (arr) => (Array.isArray(arr) ? arr : []);
      for (const v of list(this._catalog?.glassStandard)) add(v);
      for (const v of list(this._catalog?.glassPerforated)) add(v);
      for (const v of list(this._catalog?.doors)) add(v);
      for (const v of list(this._catalog?.spigots)) add(v);
      for (const v of list(this._catalog?.pinces)) add(v);
      for (const v of list(this._catalog?.doorKits)) add(v);
      for (const v of list(this._catalog?.screws)) add(v);
      return map;
    }

    _getBomItemLabel(item, variantMap, heightIn) {
      const src = String(item?.source || "").trim();
      const len = item?.lengthIn != null && Number.isFinite(Number(item.lengthIn)) ? Number(item.lengthIn) : null;
      const rampHeightIn = heightIn != null && Number.isFinite(heightIn) ? heightIn : 42;
      // Panel and door glass height is 2" less than ramp height
      const panelDoorHeightIn = Math.max(0, rampHeightIn - 2);
      const dims = len != null ? t("summaryDimensionsPo", String(Math.round(len)), String(panelDoorHeightIn)) : "";
      const v = item?.variantId ? variantMap.get(Number(item.variantId)) : null;
      let name = "";

      if (src === "door_kit") {
        const title = String(v?.title || v?.product_handle || "").toLowerCase();
        const mech =
          title.includes("polaris") ? t("summaryDoorKitMechanismPolaris") : t("summaryDoorKitMechanismRessort");
        const detailLabel = this._formatDoorDetail(item?.detail || "");
        name = detailLabel ? `${t("summaryDoorKit", mech)} (${detailLabel})` : t("summaryDoorKit", mech);
      } else if (src === "spigot") {
        const title = (v?.product_title || v?.product_handle || "").trim();
        name = title || "Spigot";
      } else if (src === "pince") {
        const title = (v?.product_title || v?.product_handle || "").trim();
        if (title) {
          name = title;
        } else {
          const mount = String(v?.metafields?.mount || "").toLowerCase();
          const angle = Number(v?.metafields?.angle_deg);
          if (mount === "glass" && angle === 180) {
            name = t("summaryClampGlass180");
          } else if (mount === "glass" && angle === 90) {
            name = t("summaryClampGlass90");
          } else if (mount === "wall" && angle === 180) {
            name = t("summaryClampWall180");
          } else {
            const label = (v?.option1 || v?.metafields?.detail || "").trim();
            name = t("summaryClampGeneric", label || "glass");
          }
        }
      } else if (src === "screw") {
        const pid = Number(v?.product_id);
        if (pid === 10276191994145) {
          name = t("summaryScrewWood");
        } else if (pid === 10276192289057) {
          name = t("summaryScrewConcrete");
        } else {
          name = t("summaryScrewGeneric");
        }
      } else if (src === "panel_custom_standard" || src === "panel_custom_perforated") {
        name =
          src === "panel_custom_perforated"
            ? t("summaryCustomPanelPerforated")
            : t("summaryCustomPanelStandard");
      } else if (src === "stair_ramp") {
        name = t("summaryStairRamp");
        const stairLen = Number(item.lengthIn);
        const stairDims = Number.isFinite(stairLen)
          ? t("summaryDimensionsPo", String(stairLen), String(rampHeightIn))
          : "";
        return stairDims ? `${name}, ${stairDims}` : name;
      } else if (src === "panel" || src === "panel_standard" || src === "panel_perforated") {
        const thickness = Number(v?.metafields?.thickness_mm);
        const thicknessLabel = Number.isFinite(thickness) ? String(thickness) : "12";
        if (src === "panel_perforated") {
          name = t("summaryPerforatedPanel", thicknessLabel);
        } else {
          name = t("summaryStandardPanel", thicknessLabel);
        }
      } else if (src === "door_glass") {
        const rawDetail = (v?.option1 || v?.metafields?.detail || "").trim();
        const detailLabel = this._formatDoorDetail(rawDetail);
        name = detailLabel || t("door");
      } else {
        name =
          (v?.option1 || v?.metafields?.detail || "").trim() ||
          (src === "panel_custom_standard" || src === "panel_custom_perforated"
            ? src === "panel_custom_perforated"
              ? t("summaryCustomPanelPerforated")
              : t("summaryCustomPanelStandard")
            : "Article");
      }
      return dims ? `${name}, ${dims}` : name;
    }

    _formatSummaryPrice(centsOrDollars) {
      const n = Number(centsOrDollars);
      if (!Number.isFinite(n)) return "";
      const value = n >= 100 || n <= -100 ? n / 100 : n;
      return value.toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "$";
    }

    _computeSpigotCountFromBom(bom, variantMap) {
      if (!Array.isArray(bom)) return 0;
      let totalSpigots = 0;
      for (const item of bom) {
        if (!item || !item.qty) continue;
        if (!String(item.source || "").includes("panel")) continue;
        if (item.source === "panel_custom_perforated" || item.source === "panel_custom_standard") {
          totalSpigots += 2 * item.qty;
          continue;
        }
        if (!item.variantId) continue;
        const v = variantMap.get(Number(item.variantId));
        const count = Number(v?.metafields?.spigots_count);
        if (Number.isFinite(count) && count > 0) totalSpigots += count * item.qty;
      }
      return totalSpigots;
    }

    _hasCustomPanelsInLayout(layoutSummary) {
      if (!layoutSummary?.sides) return false;
      for (const s of layoutSummary.sides) {
        const segments = Array.isArray(s?.segments) ? s.segments : [];
        if (segments.length) {
          for (const seg of segments) {
            const panels = Array.isArray(seg?.panels) ? seg.panels : [];
            for (const p of panels) {
              if (p?.source === "panel_custom_standard" || p?.source === "panel_custom_perforated") return true;
            }
          }
        } else {
          const panels = Array.isArray(s?.panels) ? s.panels : [];
          for (const p of panels) {
            if (p?.source === "panel_custom_standard" || p?.source === "panel_custom_perforated") return true;
          }
        }
      }
      return false;
    }

    /** Load custom products config once (for price calculation). Uses variant 52402560958753 for custom panels. */
    _getCustomProductsConfig() {
      if (this._customProductsConfig !== undefined) return this._customProductsConfig;
      if (typeof window.loadCustomProducts !== "function") {
        this._customProductsConfig = null;
        return null;
      }
      try {
        const el = document.querySelector("[data-custom-products-config]");
        this._customProductsConfig = el ? window.loadCustomProducts(el) : null;
      } catch (err) {
        this._customProductsConfig = null;
      }
      return this._customProductsConfig;
    }

    /**
     * Unit price in cents for a custom panel BOM item, using custom-products calculator.
     * Configurator custom panels: 4 corners, 0 notches, 0 cuts; 4 holes if perforated, 0 if standard.
     */
    _getCustomPanelPriceCents(item, heightIn) {
      const src = String(item?.source || "").trim();
      if (src !== "panel_custom_standard" && src !== "panel_custom_perforated") return null;
      const config = this._getCustomProductsConfig();
      if (!config || typeof window.calculateCustomProductPrice !== "function") return null;
      const cartConfig = window.CfgCartConfig || {};
      const variantId = src === "panel_custom_perforated"
        ? (Number(cartConfig.customPanelPerforatedVariantId) || 52402560958753)
        : (Number(cartConfig.customPanelVariantId) || 52402560958753);
      const productConfig = config[String(variantId)];
      if (!productConfig) return null;
      const panelHeightIn = Math.max(0, (heightIn != null && Number.isFinite(heightIn) ? heightIn : 42) - 2);
      const widthIn = item.lengthIn != null && Number.isFinite(Number(item.lengthIn)) ? Number(item.lengthIn) : 0;
      const inputs = {
        width: widthIn,
        height: panelHeightIn,
        holes: src === "panel_custom_perforated" ? 4 : 0,
        corners: 4,
        notches: 0,
        cuts: 0,
      };
      const result = window.calculateCustomProductPrice(productConfig, inputs);
      return result?.totalPriceCents != null && Number.isFinite(result.totalPriceCents) ? result.totalPriceCents : null;
    }

    /**
     * Stairs estimate in cents using the stair-ramp glass formula (12 mm Clair).
     * Returns 0 when dimensions, rates, or calculator are missing. Does not use $/marche.
     */
    _getStairsEstimateCents(cfg) {
      const stairs = cfg && Array.isArray(cfg.stairs) ? cfg.stairs : [];
      if (stairs.length === 0) return 0;
      if (typeof window.CfgStairsPrice === "undefined" || typeof window.CfgStairsPrice.estimateCents !== "function") return 0;
      if (typeof window.calculateCustomProductPrice !== "function") return 0;
      const catalog = this._getCustomProductsConfig();
      if (!catalog) return 0;
      const cartConfig = window.CfgCartConfig || {};
      const variantId = Number(cartConfig.stairRampVariantId) || window.CfgStairsPrice.DEFAULT_STAIR_RAMP_VARIANT_ID || 52525129695521;
      const productConfig = catalog[String(variantId)];
      if (!productConfig) return 0;
      const heightIn = cfg.base && Number.isFinite(cfg.base.heightIn) ? cfg.base.heightIn : 42;
      const cents = window.CfgStairsPrice.estimateCents(stairs, heightIn, productConfig, window.calculateCustomProductPrice);
      return Number.isFinite(cents) ? cents : 0;
    }

    /**
     * Priced stair-ramp product lines (one per complete stair row) for summary and cart.
     */
    _getStairRampPricedLines(cfg) {
      if (typeof window.CfgStairsPrice === "undefined" || typeof window.CfgStairsPrice.pricedLines !== "function") return [];
      if (typeof window.calculateCustomProductPrice !== "function") return [];
      const catalog = this._getCustomProductsConfig();
      if (!catalog) return [];
      const cartConfig = window.CfgCartConfig || {};
      const variantId = Number(cartConfig.stairRampVariantId) || window.CfgStairsPrice.DEFAULT_STAIR_RAMP_VARIANT_ID || 52525129695521;
      const productConfig = catalog[String(variantId)];
      if (!productConfig) return [];
      const stairs = cfg && Array.isArray(cfg.stairs) ? cfg.stairs : [];
      const heightIn = cfg && cfg.base && Number.isFinite(cfg.base.heightIn) ? cfg.base.heightIn : 42;
      const lines = window.CfgStairsPrice.pricedLines(stairs, heightIn, productConfig, window.calculateCustomProductPrice, variantId);
      return Array.isArray(lines) ? lines : [];
    }

    _populateConfiguratorSummary() {
      const input = this._collectConfiguratorInput();
      const engineOut = Engine.computeRecommendedBom(input, this._catalog);
      const heightIn = input?.base?.heightIn != null && Number.isFinite(input.base.heightIn) ? input.base.heightIn : 42;
      const variantMap = this._getSummaryVariantMap();

      const detailsEl = this._els.summaryDetails;
      const totalEl = this._els.summaryTotal;
      const bomTotalEl = this._els.summaryBomTotal;
      const stairsRowEl = this._els.summaryStairsRow;
      const stairsTotalEl = this._els.summaryStairsTotal;
      const finishNoteEl = this._els.summaryFinishNote;

      const bom = Array.isArray(engineOut?.recommendedBom) ? engineOut.recommendedBom : [];
      const cartConfig = window.CfgCartConfig || {};
      const customPanelVariantId = Number(cartConfig.customPanelVariantId) || 52402560958753;
      const customPanelPerforatedVariantId = Number(cartConfig.customPanelPerforatedVariantId) || 52402560958753;

      for (const item of bom) {
        if (!item || !(Number(item.qty) > 0)) continue;
        const src = String(item?.source || "").trim();
        if (src === "panel_custom_standard" || src === "panel_custom_perforated") {
          const unitCents = this._getCustomPanelPriceCents(item, heightIn);
          if (unitCents != null) {
            item.price = unitCents;
            item.lineTotal = (Number(item.qty) || 0) * unitCents;
          }
        }
      }

      const stairBom = this._getStairRampPricedLines(input).map((line) => ({
        source: line.source,
        variantId: line.variantId,
        qty: line.qty,
        lengthIn: line.lengthIn,
        price: line.priceCents,
        lineTotal: line.priceCents,
      }));
      const displayBom = bom.concat(stairBom);

      const bomTotalCents = displayBom.reduce((sum, it) => sum + (Number(it.lineTotal) || 0), 0);

      if (detailsEl) {
        const frag = document.createDocumentFragment();

        const customPanels = bom.filter((it) => {
          const s = String(it?.source || "").trim();
          return s === "panel_custom_standard" || s === "panel_custom_perforated";
        });
        if (customPanels.length > 0 && typeof console !== "undefined") {
          try {
            const customPanelDetails = customPanels.map((cp) => {
              const variantIdUsed = String(cp.source).includes("perforated") ? customPanelPerforatedVariantId : customPanelVariantId;
              return {
                item: cp,
                variantId: variantIdUsed,
                variant: variantMap.get(variantIdUsed) || null,
                priceCents: cp.price,
                lineTotalCents: cp.lineTotal,
              };
            });
            console.log("[RB][CustomPanels] Summary debug", {
              base: input?.base || null,
              heightIn,
              layoutSummary: engineOut?.layoutSummary || null,
              customPanels: customPanelDetails,
            });
          } catch (err) {
            // ignore logging errors
          }
        }

        for (const item of displayBom) {
          if (!item || !(Number(item.qty) > 0)) continue;
          const li = document.createElement("li");
          li.className = "cfg_summary-detail-item";
          const label = this._getBomItemLabel(item, variantMap, heightIn);
          const qty = Number(item.qty) || 0;
          const unitPrice = item.price != null && Number.isFinite(Number(item.price)) ? Number(item.price) : null;
          const lineTotal = item.lineTotal != null && Number.isFinite(Number(item.lineTotal)) ? Number(item.lineTotal) : null;
          const priceStr = unitPrice != null ? this._formatSummaryPrice(unitPrice) : "";
          const lineStr = lineTotal != null ? this._formatSummaryPrice(lineTotal) : (unitPrice == null ? t("summaryOnQuote") : "");
          const qtyStr = t("summaryQuantity", String(qty));
          li.innerHTML = "";
          const titleSpan = document.createElement("span");
          titleSpan.className = "cfg_summary-detail-title";
          titleSpan.textContent = label;
          li.appendChild(titleSpan);
          const detailsSpan = document.createElement("span");
          detailsSpan.className = "cfg_summary-detail-meta";
          detailsSpan.textContent = [priceStr, qtyStr].filter(Boolean).join(" — ");
          li.appendChild(detailsSpan);
          const totalSpan = document.createElement("span");
          totalSpan.className = "cfg_summary-detail-line-total";
          totalSpan.textContent = lineTotal != null ? this._formatSummaryPrice(lineTotal) : t("summaryOnQuote");
          li.appendChild(totalSpan);
          frag.appendChild(li);
        }
        detailsEl.innerHTML = "";
        detailsEl.appendChild(frag);
      }

      if (bomTotalEl) {
        bomTotalEl.textContent = this._formatSummaryPrice(bomTotalCents);
      }

      if (stairsRowEl) stairsRowEl.hidden = true;
      if (stairsTotalEl) stairsTotalEl.textContent = "";

      if (totalEl) {
        const grandTotalCents = bomTotalCents;
        totalEl.textContent = this._formatSummaryPrice(grandTotalCents);
        totalEl.hidden = false;
      }

      if (finishNoteEl) {
        const finish = String(input?.base?.finish || "").toLowerCase();
        let colorLabel = "";
        if (finish === "matte_black" || finish === "noir") colorLabel = t("summaryColorNoir");
        else colorLabel = t("summaryColorInox");
        if (colorLabel) {
          finishNoteEl.textContent = t("summaryFinishLabel", colorLabel);
          finishNoteEl.hidden = false;
        } else {
          finishNoteEl.hidden = true;
          finishNoteEl.textContent = "";
        }
      }

      const hasCustomPanels = this._hasCustomPanelsInLayout(engineOut?.layoutSummary);
      const hasStairs = input.stairs && input.stairs.length > 0;
      const hasValidations = Array.isArray(engineOut?.validations) && engineOut.validations.length > 0;
      const requiresPlanValidation = hasCustomPanels || hasStairs || hasValidations;

      const planYes = this.querySelector('input[name="cfg_plan_validation"][value="yes"]');
      const planNo = this.querySelector('input[name="cfg_plan_validation"][value="no"]');
      const planMsg = this._els.planValidationMsg;
      if (planNo) planNo.disabled = requiresPlanValidation;
      if (requiresPlanValidation) {
        if (planYes) planYes.checked = true;
        if (planNo) planNo.checked = false;
        if (planMsg) planMsg.hidden = false;
      } else {
        if (planNo) planNo.checked = true;
        if (planYes) planYes.checked = false;
        if (planMsg) planMsg.hidden = true;
      }
    }

    /**
     * Add current configurator BOM to cart using cart-functions (addToCart).
     * Sends one request per line item with a delay between each to avoid 429 Too Many Requests.
     * Custom panels use CfgCartConfig variant IDs and glass-panel properties (width, height, holes, etc.).
     * @param {HTMLElement} [actionButton] - Optional button to disable during processing
     */
    async _handleAddToCart(actionButton) {
      if (typeof window.addToCart !== "function" || !window.CfgPackage) {
        window.alert(t("addToCart"));
        return;
      }

      // Generate a unique bundle ID to group all items in this cart addition
      // Format: timestamp (base-36) + random suffix = ~11-12 characters
      // This ensures uniqueness across time while keeping it short for checkout display
      const bundle_id = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);

      const input = this._collectConfiguratorInput();
      const engineOut = Engine.computeRecommendedBom(input, this._catalog);
      const bom = Array.isArray(engineOut?.recommendedBom) ? engineOut.recommendedBom : [];
      const heightIn = input?.base?.heightIn != null && Number.isFinite(input.base.heightIn) ? input.base.heightIn : 42;
      const panelHeightIn = Math.max(0, heightIn - 2);

      if (bom.length === 0 || !bom.some((item) => item && Number(item.qty) > 0)) {
        window.alert(t("addToCart"));
        return;
      }

      const cartConfig = window.CfgCartConfig || {};
      const customPanelVariantId = Number(cartConfig.customPanelVariantId) || 52402560958753;
      const customPanelPerforatedVariantId = Number(cartConfig.customPanelPerforatedVariantId) || 52402560958753;

      /** @type {Array<{ id: number, quantity: number, properties?: object, files?: object }>} */
      const cartItems = [];
      const snapshotEntries = [];
      const variantMap = this._getSummaryVariantMap();

      for (const item of bom) {
        if (!item || !(Number(item.qty) > 0)) continue;

        const qty = Number(item.qty);
        const src = String(item?.source || "").trim();
        const cfgKey = window.CfgPackage.glassCfgKey(item);

        if (src === "panel_custom_standard" || src === "panel_custom_perforated") {
          const variantId = src === "panel_custom_perforated" ? customPanelPerforatedVariantId : customPanelVariantId;
          const widthIn = item.lengthIn != null && Number.isFinite(Number(item.lengthIn)) ? Number(item.lengthIn) : 0;
          const cartItem = {
            id: variantId,
            quantity: qty,
            properties: {
              width: String(Math.round(widthIn)),
              height: String(Math.round(panelHeightIn)),
              holes: src === "panel_custom_perforated" ? "4" : "0",
              corners: "4",
              notches: "0",
              cuts: "0",
              _bundle_id: bundle_id,
              _cfgKey: cfgKey,
              _cfgRecQty: String(qty)
            },
          };
          cartItems.push(cartItem);
          const catalogVariant = item.variantId ? variantMap.get(Number(item.variantId)) : null;
          const handle = (catalogVariant && (catalogVariant.product_handle || catalogVariant.handle)) || "";
          const title = (catalogVariant && (catalogVariant.product_title || catalogVariant.title)) || "";
          snapshotEntries.push({
            k: cfgKey,
            v: Number(cartItem.id),
            q: qty,
            h: handle || undefined,
            t: title || undefined,
            p: window.CfgPackage.customerProperties(cartItem.properties),
          });
          continue;
        }

        const variantId = item.variantId != null && Number.isFinite(Number(item.variantId)) ? Number(item.variantId) : null;
        if (variantId == null) continue;

        const cartItem = {
          id: variantId,
          quantity: qty,
          properties: {
            ...(item.lineItemProperties && typeof item.lineItemProperties === "object" ? item.lineItemProperties : {}),
            _bundle_id: bundle_id,
            _cfgKey: cfgKey,
            _cfgRecQty: String(qty)
          }
        };
        cartItems.push(cartItem);
        const catalogVariant = item.variantId ? variantMap.get(Number(item.variantId)) : null;
        const handle = (catalogVariant && (catalogVariant.product_handle || catalogVariant.handle)) || "";
        const title = (catalogVariant && (catalogVariant.product_title || catalogVariant.title)) || "";
        snapshotEntries.push({
          k: cfgKey,
          v: Number(cartItem.id),
          q: qty,
          h: handle || undefined,
          t: title || undefined,
          p: window.CfgPackage.customerProperties(cartItem.properties),
        });
      }

      const stairHandle = String(cartConfig.stairRampHandle || window.CfgStairsPrice && window.CfgStairsPrice.DEFAULT_STAIR_RAMP_HANDLE || "verre-trempe-pour-descente-d-escalier");
      const stairTitle = t("summaryStairRamp") || "";
      for (const line of this._getStairRampPricedLines(input)) {
        const cartItem = {
          id: Number(line.variantId),
          quantity: 1,
          properties: Object.assign({}, line.properties, {
            _bundle_id: bundle_id,
            _cfgKey: line.cfgKey,
            _cfgRecQty: "1",
          }),
        };
        cartItems.push(cartItem);
        snapshotEntries.push({
          k: line.cfgKey,
          v: Number(cartItem.id),
          q: 1,
          h: stairHandle || undefined,
          t: stairTitle || undefined,
          p: window.CfgPackage.customerProperties(cartItem.properties),
        });
      }

      // If plan validation is requested, add the design-help service product for this bundle.
      const summaryStep = this.querySelector('section[data-rb-step="cfg_summary"]');
      const summaryForm = summaryStep ? summaryStep.querySelector('form[data-rb-form]') : null;
      const validationInput = summaryForm ? summaryForm.querySelector('input[name="cfg_plan_validation"]:checked') : null;
      const planValidationValue = validationInput ? String(validationInput.value || "") : "";
      if (planValidationValue === "yes") {
        cartItems.push({
          id: Number(ESTIMATION_SERVICE_VARIANT_ID),
          quantity: 1,
          properties: {
            _bundle_id: bundle_id,
          },
        });
      }

      // Screws: 4 per spigot, based on surface type.

      let buttonState = null;
      if (actionButton && actionButton instanceof HTMLButtonElement) {
        if (typeof window.showButtonSpinner === 'function') {
          buttonState = window.showButtonSpinner(actionButton);
        } else {
          // Fallback if function not available
          actionButton.disabled = true;
          buttonState = { originalText: actionButton.textContent || '', originalWidth: '' };
        }
      }

      try {
        await window.addManyToCart(cartItems);
        await this._addProjectDataToCart(bundle_id, snapshotEntries);
        const promoApplied = await this._applyConfiguratorPromoCode();
        if (promoApplied) {
          window.location.href = '/cart';
        }
      } catch (err) {
        // Restore button on error
        if (actionButton && actionButton instanceof HTMLButtonElement && buttonState) {
          if (typeof window.restoreButton === 'function') {
            window.restoreButton(actionButton, buttonState);
          } else {
            // Fallback if function not available
            actionButton.disabled = false;
            actionButton.textContent = buttonState.originalText || '';
            if (buttonState.originalWidth) {
              actionButton.style.width = '';
            }
          }
        }
        const message = err?.message || t("addToCart");
        window.alert(message);
      }
    }

    /**
     * Apply the configurator promo code (Config15) after items are added.
     * Returns true when the cart Ajax API applied it (caller should go to /cart).
     * On failure, redirects through Shopify's /discount/{code}?redirect=/cart link instead.
     * @returns {Promise<boolean>}
     */
    async _applyConfiguratorPromoCode() {
      const promoCode = String((window.CfgCartConfig && window.CfgCartConfig.promoCode) || "").trim();
      if (!promoCode) return true;

      if (typeof window.applyDiscountCode === "function") {
        try {
          await window.applyDiscountCode(promoCode);
          return true;
        } catch (_error) {
          // Fall through to the discount landing URL.
        }
      }

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
      window.location.href = `${root}discount/${encodeURIComponent(promoCode)}?redirect=${encodeURIComponent(`${root}cart`)}`;
      return false;
    }

    async _addEstimateDataToCart() {
      // Use the same data source as _populateSoumissionPanel
      const qe = this._collectEstimateInput();
      const lines = this._getEstimateDetailLines(qe);
      
      let price = "";
      if (window.RBEstimationPrice && typeof window.RBEstimationPrice.calculate === "function") {
        const priceResult = window.RBEstimationPrice.calculate(qe);
        if (priceResult && Number.isFinite(priceResult.total)) {
          // Convert dollars to cents for formatMoney (helper-functions.js)
          const totalCents = priceResult.total * 100;
          const formattedPrice = window.formatMoney && typeof window.formatMoney === "function" 
            ? window.formatMoney(totalCents) 
            : "";
          price = formattedPrice ? `${t('fromPrice')} ${formattedPrice}`.trim() : "";
        }
      }
      
      const soumissionStep = this.querySelector('section[data-rb-step="est_soumission"]');
      const soumissionForm = soumissionStep ? soumissionStep.querySelector('form[data-rb-form]') : null;
      const installationInput = soumissionForm ? soumissionForm.querySelector('input[name="est_installation"]:checked') : null;
      const installation = installationInput ? t(installationInput.value) : "";

      const properties = {};

      // Labelled properties for price and installation
      if (price) {
        properties[t('priceLabel')] = price;
      }
      if (installation) {
        properties["Installation"] = installation;
      }

      // Sequentially labelled detail lines: "Item 1", "Item 2", ...
      // Use the same data source as _populateSoumissionPanel instead of parsing HTML
      let itemIndex = 1;
      for (const text of lines) {
        if (!text || !text.trim()) continue;
        const itemLabel = `Item ${itemIndex}`;
        properties[itemLabel] = text.trim();
        itemIndex += 1;
      }

      const attachmentsInput = soumissionForm ? soumissionForm.querySelector('input[name="est_attachments"]') : null;
      const filesPayload = buildFilesPayloadFromInput(attachmentsInput);

      const otherProjectsInputs = soumissionForm ? soumissionForm.querySelectorAll('input[name="est_other_projects[]"]:checked') : [];
      const otherProjects = Array.from(otherProjectsInputs).map(el => el.value).join(", ");
      if (otherProjects) properties[t('otherProjectsLabel')] = otherProjects;
      const additionalInfoInput = soumissionForm ? soumissionForm.querySelector('textarea[name="est_additional_info"]') : null;
      if (additionalInfoInput && additionalInfoInput.value.trim()) properties[t('additionalInfoLabel')] = additionalInfoInput.value.trim();

      await window.addToCart({
        id: ESTIMATION_SERVICE_VARIANT_ID,
        quantity: 1,
        properties,
        files: filesPayload,
      });
    }

    async _addProjectDataToCart(bundle_id, snapshotEntries) {
      const properties = {};
      const input = this._collectConfiguratorInput();
      const summaryStep = this.querySelector('section[data-rb-step="cfg_summary"]');
      const summaryForm = summaryStep ? summaryStep.querySelector('form[data-rb-form]') : null;

      const validationInput = summaryForm ? summaryForm.querySelector('input[name="cfg_plan_validation"]:checked') : null;
      const validation = validationInput ? t(validationInput.value) : "";

      const installationInput = summaryForm ? summaryForm.querySelector('input[name="cfg_installation"]:checked') : null;
      const installation = installationInput ? t(installationInput.value) : "";

      // Add finish as the first line item property
      const finish = String(input?.base?.finish || "").toLowerCase();
      if (finish) {
        let colorLabel = "";
        if (finish === "matte_black" || finish === "noir") {
          colorLabel = t("summaryColorNoir");
        } else {
          colorLabel = t("summaryColorInox");
        }
        if (colorLabel) {
          properties[t('finishLabel')] = colorLabel;
        }
      }

      if (validation) {
        properties["Validation"] = validation;
      }

      // Always attach a configuration link to the Plan & Details line item.
      const configUrl = this._getConfigSaveUrl();
      const label = t("configLinkLabel") || "Link to configuration";
      properties[label] = configUrl;

      if (installation) {
        properties["Installation"] = installation;
      }

      properties["_bundle_id"] = bundle_id;

      const encoded = window.CfgPackage.encodeRecommendedBom(snapshotEntries || []);
      Object.keys(encoded).forEach((key) => {
        properties[key] = encoded[key];
      });

      const attachmentsInput = summaryForm ? summaryForm.querySelector('input[name="cfg_summary_attachments"]') : null;
      const filesPayload = buildFilesPayloadFromInput(attachmentsInput);

      await window.addToCart({
        id: PROJECT_DETAILS_VARIANT_ID,
        quantity: 1,
        properties,
        files: filesPayload,
      });
    }
  }

  if (!customElements.get("rb-configurator")) {
    customElements.define("rb-configurator", RbConfigurator);
  }

  // Support direct linking: URL hash for step + params, or search params for full config restore.
  function initFromUrl() {
    const el = document.querySelector("rb-configurator");
    if (!(el instanceof RbConfigurator)) return;

    const searchParams = new URLSearchParams(window.location.search);
    const rawHash = window.location.hash ? window.location.hash.slice(1) : "";
    let hashStepId = "";
    let hashParamString = "";
    if (rawHash) {
      const qIndex = rawHash.indexOf("?");
      if (qIndex !== -1) {
        hashStepId = rawHash.slice(0, qIndex);
        hashParamString = rawHash.slice(qIndex + 1);
      } else {
        hashStepId = rawHash;
      }
    }

    const hasConfigParams = searchParams.has("step") || searchParams.has("cfg_shape") ||
      searchParams.has("est_railing_type") || searchParams.has("openings") || searchParams.has("stairs");

    if (hasConfigParams) {
      el._restoreFromParams(searchParams);
      return;
    }

    if (!rawHash) return;
    const stepId = hashStepId;
    if (!STEP_IDS.includes(stepId)) return;

    let productId = null;
    if (hashParamString) {
      try {
        const params = new URLSearchParams(hashParamString);
        productId = params.get("id") || productId;
      } catch (_) { /* ignore */ }
    }
    if (!productId && window.location.search) {
      productId = searchParams.get("id") || productId;
    }
    if (productId && typeof el._prefillFromProductId === "function") {
      el._prefillFromProductId(productId);
    }
    el._goToStep(stepId, { replace: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initFromUrl);
  } else {
    initFromUrl();
  }
})();

